import { useState, useEffect, useCallback, useMemo, useRef, Fragment } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Document, Page } from 'react-pdf';
import * as pdfjs from 'pdfjs-dist';
import LazyPdfPage from '../components/LazyPdfPage';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '../components/ui/resizable';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ValidationTab } from '../components/ValidationTab';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { ReparseTypeItems, useReparseConfirm, reparseToast } from '../components/reparse/ReparseOptions';
import { Badge } from '../components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { DemandSummaryCard } from '../components/DemandSummaryCard';
import { BillingBasisBadge } from '../components/BillingBasisBadge';
import api from '../lib/api';
import { getAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, FileText, Loader2, Save, Edit, X, Plus, RefreshCw, ZoomIn, ZoomOut, Check, AlertTriangle, AlertCircle, History, Trash2, Layers, FileStack, CheckCircle, Download, ExternalLink, Lock, UserCheck, ChevronDown, Copy, Info, Wand2, Clock } from 'lucide-react';
import { format } from 'date-fns';

// Use CDN worker that matches the bundled pdfjs version
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export const ConsolidatedViewer = () => {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const { confirm: confirmReparse, dialog: reparseConfirmDialog } = useReparseConfirm();
  const { user } = getAuth();
  const currentAccountStr = localStorage.getItem('current_account');
  const currentAccount = currentAccountStr ? JSON.parse(currentAccountStr) : null;
  
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [numPages, setNumPages] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reparsing, setReparsing] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  
  // PDF Zoom
  const [scale, setScale] = useState(1.0);
  const [currentPage, setCurrentPage] = useState(1);
  const pdfContainerRef = useRef(null);
  
  // Active tab
  const [activeTab, setActiveTab] = useState('statement');
  
  // BillPortal export
  const [hasBillPortalExport, setHasBillPortalExport] = useState(false);
  
  // Document workflow
  const [reviewStatus, setReviewStatus] = useState('review');
  const [assignedTo, setAssignedTo] = useState(null);
  const [accountUsers, setAccountUsers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [showAuditModal, setShowAuditModal] = useState(false);
  
  // Editing row tracking - to highlight which row is being edited
  const [editingRowId, setEditingRowId] = useState(null); // Format: 'invoices-0', 'meters-2', 'usage-5'
  const [reviewedRows, setReviewedRows] = useState(new Set()); // Rows marked as worked/checked
  const [showValidationWarnings, setShowValidationWarnings] = useState(false);
  
  // Data state - Unified schema for both single and consolidated bills
  const [statement, setStatement] = useState({
    // Property/Location Info (for consolidated: billed address)
    property_name: '',
    billed_address: '',
    city: '',
    state: '',
    postcode: '',
    // Customer Info
    billed_customer_name: '',
    parent_account_no: '',
    master_account_no: '',
    // Vendor Info
    vendor_name: '',
    commodity: '',
    // Invoice Info
    invoice_no: '',
    issue_date: '',
    due_date: '',
    period_from: '',
    period_to: '',
    // Financial Info
    total_charges: '',
    previous_balance: '',
    payments_received: '',
    balance_forward: '',
    late_fee: '',
    credits: '',
    adjustments: '',
    total_amount_due: ''
  });
  const [childInvoices, setChildInvoices] = useState([]);
  // True parent (statement-level) totals as emitted by the parser — used to
  // validate the child sum without relying on the editable statement fields
  // (which sometimes carry total_amount_due in the current-charges slot).
  const [parentTotals, setParentTotals] = useState({ new_charges: null, total_amount_due: null });
  const [meterReads, setMeterReads] = useState([]);
  const [usageSummary, setUsageSummary] = useState(null);
  const [usageDetails, setUsageDetails] = useState([]);
  const [otherCharges, setOtherCharges] = useState([]);
  const [consolidatedValidation, setConsolidatedValidation] = useState(null);
  const [recovering, setRecovering] = useState(false);
  const [revalidating, setRevalidating] = useState(false);
  // Controlled Validation-tab accordion. null = auto-derive open items from the
  // failing checks; an array = user-controlled. Revalidate resets to null so the
  // auto-expand re-derives from the freshly-recomputed validation.
  const [validationAccordion, setValidationAccordion] = useState(null);

  // Normalize commodity string to match Select options (case/whitespace tolerant).
  // Prevents existing extracted data from "disappearing" in Edit mode because
  // the Select's value doesn't match any SelectItem.
  const normalizeCommodity = (c) => {
    if (!c) return '';
    const s = String(c).trim().toLowerCase();
    if (['electric', 'electricity', 'power', 'kwh'].includes(s)) return 'Electricity';
    if (['gas', 'natural gas', 'ng'].includes(s)) return 'Gas';
    if (['water', 'w'].includes(s)) return 'Water';
    if (['sewer', 'sewage', 'wastewater'].includes(s)) return 'Sewer';
    if (['waste', 'trash', 'garbage', 'refuse'].includes(s)) return 'Waste';
    // Title-case fallback so unknown values still render in Select (won't match
    // options but won't blank the field either).
    return String(c).trim();
  };

  // Normalize date strings to YYYY-MM-DD so HTML <input type="date"> can render
  // existing extracted values. Accepts MM/DD/YYYY, M/D/YYYY, DD-MM-YYYY, and
  // already-ISO formats. Returns '' for invalid or blank input.
  const normalizeDate = (d) => {
    if (!d) return '';
    const s = String(d).trim();
    if (!s || ['N/A', 'None', 'null', '-'].includes(s)) return '';
    // Already ISO YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    // MM/DD/YYYY or M/D/YYYY
    let m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (m) {
      const [, mm, dd, yyyy] = m;
      return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
    }
    // DD-MM-YYYY
    m = s.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
    if (m) {
      const [, dd, mm, yyyy] = m;
      return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
    }
    // Fallback: Date parser
    const dt = new Date(s);
    if (!isNaN(dt.getTime())) {
      const yyyy = dt.getFullYear();
      const mm = String(dt.getMonth() + 1).padStart(2, '0');
      const dd = String(dt.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    }
    return '';
  };

  // Numeric-only sanitizer & due-date validation
  const isValidAmountConsolidated = (val) => val === '' || /^-?\d*\.?\d*$/.test(String(val));
  const handleAmountChange = (field) => (e) => {
    const v = e.target.value;
    if (isValidAmountConsolidated(v)) {
      setStatement((prev) => ({ ...prev, [field]: v }));
    }
  };
  // Invoice-No: accept any value (real bills legitimately contain letters).
  const handleInvoiceNoChange = (e) => {
    setStatement((prev) => ({ ...prev, invoice_no: e.target.value }));
  };
  const dueDateError = useMemo(() => {
    const iss = statement.issue_date;
    const due = statement.due_date;
    if (iss && due && due < iss) {
      return 'Due date cannot be earlier than issue date';
    }
    return '';
  }, [statement.issue_date, statement.due_date]);

  // Mandatory statement-level fields validation.
  // Property Name is NOT mandatory (auto-filled from onboarding / service address).
  // Period From/To are only mandatory for metered utility commodities
  // (Electricity, Gas, Water); optional for all other commodities.
  const mandatoryFieldsValidation = useMemo(() => {
    const periodRequired = ['electricity', 'gas', 'water'].includes(
      String(statement.commodity || '').trim().toLowerCase()
    );
    const MANDATORY = [
      { key: 'vendor_name', label: 'Vendor Name' },
      { key: 'parent_account_no', label: 'Parent Account No' },
      { key: 'billed_customer_name', label: 'Billed Customer Name' },
      { key: 'issue_date', label: 'Issue Date' },
      ...(periodRequired
        ? [{ key: 'period_from', label: 'Period From' }, { key: 'period_to', label: 'Period To' }]
        : []),
      { key: 'total_charges', label: 'Current Charges (New Charges)' },
      { key: 'total_amount_due', label: 'Total Amount Due' },
    ];
    const isBlank = (v) => {
      if (v === null || v === undefined) return true;
      const s = String(v).trim();
      return s === '' || s === 'N/A' || s === 'None' || s === 'null';
    };
    const missing = MANDATORY.filter((f) => isBlank(statement[f.key]));
    return {
      total: MANDATORY.length,
      missing,
      allPresent: missing.length === 0,
      missingKeys: new Set(missing.map((f) => f.key)),
    };
  }, [
    statement.commodity, statement.vendor_name, statement.parent_account_no,
    statement.billed_customer_name, statement.issue_date, statement.period_from,
    statement.period_to, statement.total_charges, statement.total_amount_due
  ]);

  // Total validation calculation
  const totalValidation = useMemo(() => {
    const parseAmount = (val) => {
      if (!val || val === '' || val === 'None') return 0;
      const cleaned = String(val).replace(/[$,]/g, '').trim();
      return parseFloat(cleaned) || 0;
    };

    // Sum the accounts on a like-for-like basis (NEVER add balance-brought-forward
    // to current charges — the prior balances were already paid and are not part
    // of either the current charges or the total amount due).
    const sumNewCharges = childInvoices.reduce((s, c) => s + parseAmount(c.new_charges), 0);
    const sumTotalDue = childInvoices.reduce((s, c) => s + parseAmount(c.total_amount_due), 0);

    // Statement basis — prefer the TRUE parent figures emitted by the parser,
    // then fall back to the (editable) statement fields.
    const stmtNewCharges = parseAmount(parentTotals.new_charges) || parseAmount(statement.new_charges);
    const stmtTotalDue = parseAmount(parentTotals.total_amount_due)
      || parseAmount(statement.total_amount_due) || parseAmount(statement.total_charges);

    // Validate on the Total Amount Due (a clean invariant: Σ accounts' total due
    // == the bill's total due, and both are reliably populated). Fall back to
    // Current Charges when children don't carry a total_amount_due.
    let basis, statementTotal, childTotal;
    if (sumTotalDue > 0 && stmtTotalDue > 0) {
      basis = 'Total Amount Due'; statementTotal = stmtTotalDue; childTotal = sumTotalDue;
    } else if (stmtNewCharges > 0) {
      basis = 'Current Charges'; statementTotal = stmtNewCharges; childTotal = sumNewCharges;
    } else {
      basis = 'Total Amount Due'; statementTotal = stmtTotalDue; childTotal = sumTotalDue || sumNewCharges;
    }

    const variance = Math.abs(statementTotal - childTotal);
    const variancePercent = statementTotal > 0 ? (variance / statementTotal) * 100 : 0;
    const isValid = variance <= 1 || variancePercent <= 1;

    return {
      basis,
      sumNewCharges: sumNewCharges.toFixed(2),
      sumTotalDue: sumTotalDue.toFixed(2),
      sumChildTotal: childTotal.toFixed(2),
      statementTotal: statementTotal.toFixed(2),
      variance: variance.toFixed(2),
      variancePercent: variancePercent.toFixed(1),
      isValid,
      hasData: childInvoices.length > 0 && statementTotal > 0,
    };
  }, [childInvoices, statement.total_amount_due, statement.total_charges, statement.new_charges, parentTotals]);

  // Filter usage to show only usage items (exclude taxes, fees, etc.)
  const filteredUsageDetails = useMemo(() => {
    // Keywords that indicate non-usage line items to exclude
    const excludeKeywords = [
      'tax', 'sales tax', 'state tax', 'local tax', 'fee', 'surcharge', 
      'customer charge', 'service charge', 'minimum charge', 'late fee',
      'admin', 'regulatory', 'franchise', 'gross receipts', 'adjustment'
    ];
    
    // Filter out non-usage items
    const usageOnly = usageDetails.filter(item => {
      const desc = (item.line_item_description || '').toLowerCase();
      const hasExcluded = excludeKeywords.some(keyword => desc.includes(keyword));
      // Keep items that have usage quantity != 0 (including negative for credits) and are not excluded
      const hasUsage = parseFloat(item.usage_quantity || 0) !== 0;
      return !hasExcluded && hasUsage;
    });
    
    // For Gas: If both CCF and Therms exist for same account/meter, prefer Therms
    const accountMeterMap = new Map();
    usageOnly.forEach(item => {
      const key = `${item.child_account_no || ''}-${item.meter_number || ''}`;
      const uom = (item.uom || '').toLowerCase();
      if (!accountMeterMap.has(key)) {
        accountMeterMap.set(key, { ccf: [], therms: [], other: [] });
      }
      if (uom === 'ccf') {
        accountMeterMap.get(key).ccf.push(item);
      } else if (uom === 'therms' || uom === 'therm') {
        accountMeterMap.get(key).therms.push(item);
      } else {
        accountMeterMap.get(key).other.push(item);
      }
    });
    
    // Build final list: prefer therms over CCF for gas
    const finalItems = [];
    accountMeterMap.forEach((items) => {
      // If both CCF and Therms exist, only include Therms
      if (items.therms.length > 0 && items.ccf.length > 0) {
        finalItems.push(...items.therms);
      } else {
        // Otherwise include both
        finalItems.push(...items.ccf, ...items.therms);
      }
      // Always include non-gas items
      finalItems.push(...items.other);
    });
    
    return finalItems;
  }, [usageDetails]);
  
  // Calculate unique meter count for Meter tab
  const uniqueMeterCount = useMemo(() => {
    const uniqueMeters = new Set();
    meterReads.forEach(meter => {
      const meterKey = meter.meter_number || meter.child_account_no || '';
      if (meterKey) uniqueMeters.add(meterKey);
    });
    return uniqueMeters.size;
  }, [meterReads]);
  
  // Calculate unique account/meter count for Usage tab
  const uniqueUsageCount = useMemo(() => {
    const uniqueKeys = new Set();
    filteredUsageDetails.forEach(usage => {
      const key = `${usage.child_account_no || ''}-${usage.meter_number || ''}`;
      uniqueKeys.add(key);
    });
    return uniqueKeys.size;
  }, [filteredUsageDetails]);

  // Date validation helper - validates date fields for child invoices
  const validateInvoiceDates = useCallback((invoice) => {
    const validations = {
      due_date: { isValid: true, message: '' },
      period_from: { isValid: true, message: '' },
      period_to: { isValid: true, message: '' },
      period_range: { isValid: true, message: '' }
    };
    
    const parseDate = (dateStr) => {
      if (!dateStr || dateStr === '' || dateStr === 'None') return null;
      const d = new Date(dateStr);
      return isNaN(d.getTime()) ? null : d;
    };
    
    const dueDate = parseDate(invoice.due_date);
    const periodFrom = parseDate(invoice.period_from);
    const periodTo = parseDate(invoice.period_to);
    const today = new Date();
    
    // Due date validation
    if (!dueDate) {
      validations.due_date = { isValid: false, message: 'Due date missing' };
    } else if (dueDate < new Date(today.getFullYear() - 2, 0, 1)) {
      validations.due_date = { isValid: false, message: 'Due date seems too old' };
    }
    
    // Period From validation
    if (!periodFrom) {
      validations.period_from = { isValid: false, message: 'Period start missing' };
    }
    
    // Period To validation
    if (!periodTo) {
      validations.period_to = { isValid: false, message: 'Period end missing' };
    }
    
    // Period range validation (period_to should be after period_from)
    if (periodFrom && periodTo && periodTo < periodFrom) {
      validations.period_range = { isValid: false, message: 'Period end before start' };
    }
    
    // Period should be reasonable (not more than 90 days typically)
    if (periodFrom && periodTo) {
      const daysDiff = (periodTo - periodFrom) / (1000 * 60 * 60 * 24);
      if (daysDiff > 90) {
        validations.period_range = { isValid: false, message: `Period spans ${Math.round(daysDiff)} days` };
      }
    }
    
    return validations;
  }, []);

  // Calculate date validations for all child invoices
  const invoiceDateValidations = useMemo(() => {
    return childInvoices.map(invoice => validateInvoiceDates(invoice));
  }, [childInvoices, validateInvoiceDates]);

  // Meter read date validation
  const meterValidation = useMemo(() => {
    const validations = meterReads.map((read, index) => {
      const prevDate = read.previous_read_date;
      const currDate = read.current_read_date;
      
      if (!prevDate || !currDate) {
        return { index, isValid: true, message: '' }; // Skip if dates missing
      }
      
      const prevDateObj = new Date(prevDate);
      const currDateObj = new Date(currDate);
      
      if (isNaN(prevDateObj.getTime()) || isNaN(currDateObj.getTime())) {
        return { index, isValid: true, message: '' }; // Skip invalid dates
      }
      
      if (prevDateObj >= currDateObj) {
        return {
          index,
          isValid: false,
          message: `Meter ${read.meter_number || index + 1}: Previous read date (${prevDate}) must be before current read date (${currDate})`
        };
      }
      
      return { index, isValid: true, message: '' };
    });
    
    const hasErrors = validations.some(v => !v.isValid);
    const errorMessages = validations.filter(v => !v.isValid).map(v => v.message);
    
    return { validations, hasErrors, errorMessages };
  }, [meterReads]);

  // Usage line item validation (usage × rate = charge)
  const usageValidation = useMemo(() => {
    const parseNum = (val) => {
      if (!val || val === '' || val === 'None' || val === '-') return null;
      const cleaned = String(val).replace(/[$,]/g, '').trim();
      const num = parseFloat(cleaned);
      return isNaN(num) ? null : num;
    };

    const validations = filteredUsageDetails.map((item, index) => {
      const usage = parseNum(item.usage_quantity);
      const rate = parseNum(item.rate);
      const charge = parseNum(item.charge);
      
      // Skip validation if any required field is missing
      if (usage === null || rate === null || charge === null) {
        return { index, isValid: true, message: '', calculated: null };
      }
      
      const calculated = usage * rate;
      const difference = Math.abs(calculated - charge);
      const tolerance = Math.max(0.02, Math.abs(charge * 0.01)); // 1% or $0.02 tolerance
      
      if (difference > tolerance) {
        return {
          index,
          isValid: false,
          message: `Row ${index + 1}: ${usage} × ${rate} = ${calculated.toFixed(2)}, but charge shows ${charge.toFixed(2)}`,
          calculated
        };
      }
      
      return { index, isValid: true, message: '', calculated };
    });
    
    const hasErrors = validations.some(v => !v.isValid);
    const errorMessages = validations.filter(v => !v.isValid).map(v => v.message);
    
    return { validations, hasErrors, errorMessages };
  }, [filteredUsageDetails]);

  // UOM validation based on commodity pattern
  const uomValidation = useMemo(() => {
    const expectedUOM = {
      'Electricity': ['kWh', 'kwh', 'KWH', 'MWh', 'mwh'],
      'Gas': ['Therms', 'therms', 'THERMS', 'CCF', 'ccf', 'MCF', 'Dth', 'DTH'],
      'Water': ['Gallons', 'gallons', 'GAL', 'gal', 'HCF', 'hcf', 'CCF', 'ccf', 'kGal', 'KGAL'],
      'Sewer': ['Gallons', 'gallons', 'GAL', 'gal', 'HCF', 'hcf', 'CCF', 'ccf']
    };

    // Count UOM patterns by commodity to validate against majority
    const commodityUomCounts = {};
    filteredUsageDetails.forEach(item => {
      const commodity = item.commodity || statement.commodity;
      const uom = item.uom;
      if (commodity && uom) {
        if (!commodityUomCounts[commodity]) commodityUomCounts[commodity] = {};
        commodityUomCounts[commodity][uom] = (commodityUomCounts[commodity][uom] || 0) + 1;
      }
    });

    const validations = filteredUsageDetails.map((item, index) => {
      const commodity = item.commodity || statement.commodity;
      const uom = item.uom;
      
      if (!commodity || !uom) {
        return { index, isValid: true, message: '' };
      }
      
      const expected = expectedUOM[commodity] || [];
      if (expected.length === 0) {
        return { index, isValid: true, message: '' };
      }
      
      const isValidUOM = expected.some(e => uom.toLowerCase().includes(e.toLowerCase()));
      
      if (!isValidUOM) {
        return {
          index,
          isValid: false,
          message: `Row ${index + 1}: UOM "${uom}" unexpected for ${commodity}. Expected: ${expected.slice(0, 3).join(', ')}`
        };
      }
      
      return { index, isValid: true, message: '' };
    });
    
    const hasErrors = validations.some(v => !v.isValid);
    const errorMessages = validations.filter(v => !v.isValid).map(v => v.message);
    
    // Calculate pattern validation summary
    const patternSummary = Object.entries(commodityUomCounts).map(([commodity, uoms]) => {
      const total = Object.values(uoms).reduce((a, b) => a + b, 0);
      const mostCommon = Object.entries(uoms).sort((a, b) => b[1] - a[1])[0];
      return { commodity, mostCommonUOM: mostCommon?.[0], count: mostCommon?.[1], total };
    });
    
    return { validations, hasErrors, errorMessages, patternSummary };
  }, [filteredUsageDetails, statement.commodity]);

  useEffect(() => {
    if (!currentAccount) {
      toast.error('No account selected');
      navigate('/bill-jobs');
      return;
    }
    fetchDocument();
    fetchPdfAsBlob();
    fetchAccountUsers();
    fetchAuditLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId, currentAccount?.id, navigate]);

  const fetchAccountUsers = async () => {
    if (!currentAccount) return;
    try {
      const { data } = await api.get(`/accounts/${currentAccount.id}/users-list`);
      setAccountUsers(data);
    } catch (error) {
      console.error('Failed to load users');
    }
  };

  const fetchAuditLogs = async () => {
    if (!currentAccount) return;
    try {
      const { data } = await api.get(`/accounts/${currentAccount.id}/documents/${documentId}/audit`);
      setAuditLogs(data);
    } catch (error) {
      console.error('Failed to load audit logs');
    }
  };

  const fetchPdfAsBlob = async () => {
    if (!currentAccount) return;
    try {
      // First get file info (type and URL)
      const response = await api.get(
        `/accounts/${currentAccount.id}/documents/${documentId}/file`
      );
      
      if (response.data.type === 's3') {
        // Stream the PDF straight from the S3 presigned URL. pdf.js then uses
        // HTTP range requests + lazy page fetching (see pdfOptions) so the first
        // page renders almost immediately instead of waiting for the whole file
        // to download as a blob first.
        setPdfUrl(response.data.url);
      } else {
        // Local file - use the download endpoint
        const blobResponse = await api.get(
          `/accounts/${currentAccount.id}/documents/${documentId}/file/download`,
          { responseType: 'blob' }
        );
        const blob = new Blob([blobResponse.data], { type: 'application/pdf' });
        setPdfUrl(URL.createObjectURL(blob));
      }
    } catch (error) {
      console.error('Failed to load PDF:', error);
      toast.error('Failed to load PDF file');
    }
  };

  const fetchDocument = async () => {
    if (!currentAccount) return;
    try {
      const { data } = await api.get(`/accounts/${currentAccount.id}/documents/${documentId}`);
      setDocument(data.document);
      
      // Set document workflow state
      setReviewStatus(data.document.review_status || 'review');
      setAssignedTo(data.document.assigned_to || null);
      
      // Check if BillPortal export exists
      setHasBillPortalExport(data.document.billportal_exported || false);
      
      if (data.extracted_data) {
        // Normalize commodity/date fields so existing data renders in Edit mode
        const normStatement = { ...(data.extracted_data.statement || {}) };
        if (normStatement.commodity) normStatement.commodity = normalizeCommodity(normStatement.commodity);
        // Lambda emits statement.new_charges; the editor field is total_charges.
        if ((normStatement.total_charges === undefined || normStatement.total_charges === null || normStatement.total_charges === '') && normStatement.new_charges) {
          normStatement.total_charges = normStatement.new_charges;
        }
        // The parser sometimes drops TOTAL AMOUNT DUE into the current-charges
        // slot. Correct it from the true parent new_charges so "Current Charges"
        // reads cleanly (only when it clearly mirrors the total and the parent
        // current charges differ by more than $1).
        const _num = (v) => { const n = parseFloat(String(v ?? '').replace(/[$,]/g, '').trim()); return Number.isFinite(n) ? n : 0; };
        const _parentNew = _num(data.extracted_data.new_charges);
        const _parentDue = _num(data.extracted_data.total_amount_due);
        if (_parentNew > 0
            && Math.abs(_num(normStatement.total_charges) - _num(normStatement.total_amount_due)) < 0.01
            && Math.abs(_num(normStatement.total_charges) - _parentNew) > 1) {
          normStatement.total_charges = data.extracted_data.new_charges;
        }
        setParentTotals({
          new_charges: data.extracted_data.new_charges ?? null,
          total_amount_due: data.extracted_data.total_amount_due ?? null,
        });
        ['issue_date', 'due_date', 'period_from', 'period_to'].forEach(k => {
          if (normStatement[k]) normStatement[k] = normalizeDate(normStatement[k]);
        });

        const normChildren = (data.extracted_data.child_invoices || []).map(inv => ({
          ...inv,
          commodity: inv.commodity ? normalizeCommodity(inv.commodity) : inv.commodity,
          due_date: normalizeDate(inv.due_date),
          period_from: normalizeDate(inv.period_from),
          period_to: normalizeDate(inv.period_to),
        }));

        const normMeters = (data.extracted_data.meter_reads || []).map(m => ({
          ...m,
          commodity: m.commodity ? normalizeCommodity(m.commodity) : m.commodity,
          previous_read_date: normalizeDate(m.previous_read_date),
          current_read_date: normalizeDate(m.current_read_date),
        }));

        const normUsage = (data.extracted_data.usage_details || []).map(u => ({
          ...u,
          commodity: u.commodity ? normalizeCommodity(u.commodity) : u.commodity,
          period_from: normalizeDate(u.period_from),
          period_to: normalizeDate(u.period_to),
        }));

        // Set consolidated bill data
        setStatement(normStatement);
        setChildInvoices(normChildren);
        setMeterReads(normMeters);
        setUsageDetails(normUsage);
        setOtherCharges(data.extracted_data.other_charges || []);
        setConsolidatedValidation(data.extracted_data._consolidated_validation || null);
        setUsageSummary(
          data.extracted_data.usage_summary && data.extracted_data.usage_summary.per_meter
            ? data.extracted_data.usage_summary
            : null
        );
      }
    } catch (error) {
      console.error('Failed to load document:', error);
      toast.error('Failed to load document');
      navigate('/bill-jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // Client-side validation: due date >= issue date
    if (dueDateError) {
      toast.error(dueDateError);
      return;
    }
    // Mandatory-field guard: block save until required statement fields are present.
    if (!mandatoryFieldsValidation.allPresent) {
      toast.error(
        `Unable to save — missing mandatory fields: ${mandatoryFieldsValidation.missing.map((f) => f.label).join(', ')}`,
        { duration: 8000 }
      );
      return;
    }
    setSaving(true);
    try {
      const updatedData = {
        bill_type: 'consolidated',
        statement: {
          ...statement,
          new_charges: statement.total_charges || statement.new_charges,
          current_charges: statement.total_charges || statement.current_charges,
        },
        child_invoices: childInvoices,
        meter_reads: meterReads,
        usage_details: usageDetails,
        other_charges: otherCharges,
        child_count: childInvoices.length
      };
      
      await api.put(`/accounts/${currentAccount.id}/documents/${documentId}/extracted-data`, {
        data: updatedData
      });
      
      toast.success('Changes saved successfully');
      setEditMode(false);
      fetchDocument();
    } catch (error) {
      console.error('Failed to save:', error);
      const detail = error.response?.data?.detail;
      const serverMsg = typeof detail === 'string'
        ? detail
        : Array.isArray(detail)
          ? detail.map((d) => d.msg || JSON.stringify(d)).join('; ')
          : null;
      if (!mandatoryFieldsValidation.allPresent) {
        toast.error(
          `Unable to save — missing mandatory fields: ${mandatoryFieldsValidation.missing.map((f) => f.label).join(', ')}${serverMsg ? ` (${serverMsg})` : ''}`,
          { duration: 8000 }
        );
      } else {
        toast.error(serverMsg || 'Failed to save changes', { duration: 8000 });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleReparse = (mode = 'all', force = null) =>
    confirmReparse(document, () => runReparse(mode, force));

  const runReparse = async (mode = 'all', force = null) => {
    setReparsing(true);
    try {
      const { data: rp } = await api.post(`/accounts/${currentAccount.id}/documents/${documentId}/reprocess`, {
        mode: mode,  // 'all' or 'missing'
        force_bill_type: force,
      });
      if (rp?.processing_method === 'postprocess') {
        setReparsing(false);
        toast.success(reparseToast(rp), { duration: 7000 });
        if (rp.action === 'split') navigate('/bill-jobs');
        else fetchDocument();
        return;
      }
      if (rp?.held) {
        setReparsing(false);
        toast.error(rp.detail || 'Bill limit reached', {
          duration: 8000,
          description: 'Upgrade a plan or buy bill credits to re-parse this bill.',
        });
        return;
      }
      toast.success(mode === 'missing' ? 'Re-parsing missing fields. This can take 1-3 minutes...' : 'Full re-parsing started. This can take 1-3 minutes...');
      // Poll until parsing finishes (Lambda takes 60-180s) instead of
      // refreshing after 5s and showing stale "same output".
      const started = Date.now();
      const poll = async () => {
        if (Date.now() - started > 4 * 60 * 1000) {
          toast.warning('Re-parse is taking longer than expected — refresh the page in a minute.');
          setReparsing(false);
          return;
        }
        try {
          const res = await api.get(`/accounts/${currentAccount.id}/documents/${documentId}`);
          const st = res.data?.document?.status || res.data?.status;
          if (st && st !== 'processing' && st !== 'queued') {
            if (force === 'collective') { setReparsing(false); navigate('/bill-jobs'); return; }
            if (force === 'single') { setReparsing(false); navigate(`/viewer/${documentId}`); return; }
            await fetchDocument();
            setReparsing(false);
            toast.success(st === 'completed' ? 'Re-parse complete — data refreshed.' : `Re-parse finished with status: ${st}`);
            return;
          }
        } catch (e) { /* keep polling */ }
        setTimeout(poll, 5000);
      };
      setTimeout(poll, 8000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start re-parsing');
      setReparsing(false);
    }
  };

  const handleRecover = async () => {
    setRecovering(true);
    try {
      await api.post(`/accounts/${currentAccount.id}/documents/${documentId}/recover-lineitems`);
      toast.success('Recovering missing line items with AI — this runs in the background.');
      const started = Date.now();
      const poll = async () => {
        if (Date.now() - started > 5 * 60 * 1000) {
          toast.warning('Recovery is taking longer than expected — refresh the page in a minute.');
          setRecovering(false);
          return;
        }
        try {
          const { data } = await api.get(`/accounts/${currentAccount.id}/documents/${documentId}/recover-lineitems`);
          const st = data?.job?.status;
          if (st === 'completed' || st === 'failed') {
            setRecovering(false);
            if (st === 'completed') {
              const r = data.job.result || {};
              const after = r.after || {};
              await fetchDocument();
              fetchAuditLogs();
              const meterMsg = r.recovered_meters_count
                ? ` Refreshed meter reads for ${r.recovered_meters_count} account(s).`
                : '';
              toast.success(
                `Recovery complete — recovered ${r.recovered_count || 0} account(s).` + meterMsg + ' ' +
                `${after.children_reconciled ?? '?'} / ${after.children_total ?? '?'} accounts now reconcile.`,
                { duration: 9000 }
              );
            } else {
              toast.error(data.job.error || 'Recovery failed. Please try again.');
            }
            return;
          }
        } catch (e) { /* keep polling */ }
        setTimeout(poll, 4000);
      };
      setTimeout(poll, 4000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start recovery');
      setRecovering(false);
    }
  };

  const handleStatusChange = async (newStatus) => {    try {
      await api.patch(`/accounts/${currentAccount.id}/documents/${documentId}/status`, {
        review_status: newStatus
      });
      setReviewStatus(newStatus);
      toast.success(`Status changed to ${newStatus}`);
      fetchAuditLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleAssignment = async (userId) => {
    try {
      await api.patch(`/accounts/${currentAccount.id}/documents/${documentId}/assign`, {
        user_id: userId || null
      });
      const assignedUser = accountUsers.find(u => u.id === userId);
      setAssignedTo(userId);
      toast.success(userId ? `Assigned to ${assignedUser?.name}` : 'Assignment removed');
      fetchDocument();
      fetchAuditLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign');
    }
  };

  // Memoize PDF.js options to avoid unnecessary reloads
  const pdfOptions = useMemo(() => ({
    cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
    cMapPacked: true,
    standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/standard_fonts/`,
    wasmUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/wasm/`,
    // Stream large PDFs: pdf.js fetches only the byte-ranges needed for the
    // visible pages (paired with LazyPdfPage) so the first page paints fast on
    // big consolidated bills instead of downloading the entire file up front.
    disableStream: false,
    disableAutoFetch: true,
    rangeChunkSize: 65536,
  }), []);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  // Track visible PDF page while user scrolls so "Page X of Y" stays in sync
  useEffect(() => {
    if (!numPages || !pdfContainerRef.current) return;
    const rootEl = pdfContainerRef.current;
    const io = new IntersectionObserver(
      (entries) => {
        // Pick the page whose intersection ratio with the scroll container is highest
        let best = null;
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          if (!best || entry.intersectionRatio > best.intersectionRatio) {
            best = entry;
          }
        });
        if (best) {
          const pageNum = parseInt(best.target.getAttribute('data-page-number'), 10);
          if (pageNum && !Number.isNaN(pageNum)) {
            setCurrentPage((prev) => (prev === pageNum ? prev : pageNum));
          }
        }
      },
      { root: rootEl, threshold: [0.25, 0.5, 0.75] }
    );
    // Observe once react-pdf has rendered pages (poll briefly)
    const attach = () => {
      const pages = rootEl.querySelectorAll('[data-page-number]');
      pages.forEach((el) => io.observe(el));
      return pages.length > 0;
    };
    if (!attach()) {
      const t = setTimeout(attach, 500);
      return () => { clearTimeout(t); io.disconnect(); };
    }
    return () => io.disconnect();
  }, [numPages, pdfUrl]);

  const handleZoomIn = () => setScale(prev => Math.min(prev + 0.1, 3));
  const handleZoomOut = () => setScale(prev => Math.max(prev - 0.1, 0.5));
  const handleZoomReset = () => setScale(1.0);
  
  // Navigate to specific page in PDF
  const navigateToPage = useCallback((pageNum) => {
    if (!pageNum || !numPages) return;
    const page = parseInt(pageNum, 10);
    if (page < 1 || page > numPages) return;
    
    setCurrentPage(page);
    
    // Scroll to the page element
    if (pdfContainerRef.current) {
      const pageElement = pdfContainerRef.current.querySelector(`[data-page-number="${page}"]`);
      if (pageElement) {
        pageElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
    
    toast.info(`Navigating to page ${page}`, { duration: 1500 });
  }, [numPages]);

  // Handle row focus - highlight row and navigate to page (text highlighting disabled)
  const handleRowFocus = useCallback((rowId, pageNumber) => {
    setEditingRowId(rowId);
    if (pageNumber) {
      navigateToPage(pageNumber);
    }
  }, [navigateToPage]);

  // Mark row as reviewed/worked
  const toggleRowReviewed = useCallback((rowId) => {
    setReviewedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  }, []);

  // Download JSON handler
  const handleDownloadJSON = async () => {
    if (!currentAccount || !documentId) return;
    const convertUrl = `/accounts/${currentAccount.id}/parser-exports/${documentId}/convert`;
    const fetchUrl = `/accounts/${currentAccount.id}/parser-exports/by-document/${documentId}`;
    try {
      // Always re-convert on download so the returned JSON reflects the
      // latest saved edits (child invoices, meter reads, other charges).
      toast.info('Refreshing BillPortal JSON…');
      await api.post(convertUrl);
      setHasBillPortalExport(true);

      // Fetch the export for THIS document directly.  The previous
      // implementation used GET /parser-exports (list, limit=50) + client-side
      // filter which failed with "Export data not found" for orgs with many
      // consolidated bills.
      const { data: exportData } = await api.get(fetchUrl);

      if (exportData) {
        // Create and download JSON file
        const jsonStr = JSON.stringify(exportData, null, 2);
        const blob = new Blob([jsonStr], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = window.document.createElement('a');
        link.href = url;
        link.download = `${document?.filename?.replace('.pdf', '') || 'bill'}_billportal.json`;
        link.click();
        URL.revokeObjectURL(url);
        toast.success('JSON downloaded');
      } else {
        toast.error('Export data not found');
      }
    } catch (error) {
      const status = error?.response?.status;
      if (status === 404) {
        toast.error('Export data not found — try re-parsing this bill');
      } else {
        toast.error('Failed to download JSON');
      }
      console.error(error);
    }
  };

  // Child invoice handlers
  const addChildInvoice = () => {
    setChildInvoices([...childInvoices, {
      customer_name: '',
      unit_address: '',
      child_account_no: '',
      child_invoice_no: '',
      new_charges: '0',
      total_amount_due: '0',
      balance_brought_forward: '0',
      late_fee: '0',
      sales_tax: '0',
      issue_date: '',
      period_from: '',
      period_to: '',
      due_date: '',
      review_status: 'review'
    }]);
  };

  const updateChildInvoice = (index, field, value) => {
    const updated = [...childInvoices];
    updated[index] = { ...updated[index], [field]: value };
    setChildInvoices(updated);
  };

  const removeChildInvoice = (index) => {
    if (window.confirm('Are you sure you want to remove this child invoice?')) {
      setChildInvoices(childInvoices.filter((_, i) => i !== index));
    }
  };

  // Paste-All functionality for Summary Tab
  const pasteAllField = (field, sourceIndex = 0) => {
    if (childInvoices.length === 0) return;
    const sourceValue = childInvoices[sourceIndex]?.[field] || '';
    if (!sourceValue) {
      toast.error('No value in first row to copy');
      return;
    }
    setChildInvoices(childInvoices.map(inv => ({ ...inv, [field]: sourceValue })));
    toast.success(`Copied "${field.replace(/_/g, ' ')}" to all rows`);
  };

  // Paste-All functionality for Meter Tab
  const pasteAllMeterField = (field, sourceIndex = 0) => {
    if (meterReads.length === 0) return;
    const sourceValue = meterReads[sourceIndex]?.[field] || '';
    if (!sourceValue) {
      toast.error('No value in first row to copy');
      return;
    }
    setMeterReads(meterReads.map(meter => ({ ...meter, [field]: sourceValue })));
    toast.success(`Copied "${field.replace(/_/g, ' ')}" to all rows`);
  };

  // Meter read handlers
  const addMeterRead = () => {
    setMeterReads([...meterReads, {
      child_account_no: '',
      meter_number: '',
      previous_read_date: '',
      current_read_date: '',
      previous_read: '',
      current_read: '',
      read_type: 'Actual'
    }]);
  };

  // Commodity → allowed UOM options (shared between usage_details and meter_reads)
  const UOM_BY_COMMODITY = {
    Electricity:   ['kWh', 'MWh', 'kW'],
    Gas:           ['Therms', 'MCF', 'CCF', 'cu ft', 'MMBtu'],
    Water:         ['Gallons', 'CCF', 'Cubic Meters', 'Litres'],
    Sewer:         ['Gallons', 'CCF', 'Cubic Meters'],
    Waste:         ['Tons', 'lbs', 'Yards'],
    'Heating Oil': ['Gallons', 'Litres'],
    Internet:      ['GB', 'TB', 'Mbps'],
    Phone:         ['Minutes', 'Units'],
    'Cable/TV':    ['Units'],
    Other:         ['Units', 'kWh', 'Therms', 'CCF', 'Gallons'],
  };
  const uomOptionsFor = (commodity) => UOM_BY_COMMODITY[commodity] || UOM_BY_COMMODITY.Other;
  const isNumericOnly = (val) => val === '' || /^-?\d*\.?\d*$/.test(String(val));

  const updateMeterRead = (index, field, value) => {
    const updated = [...meterReads];
    updated[index] = { ...updated[index], [field]: value };
    if (field === 'commodity') {
      const validUoms = uomOptionsFor(value);
      if (updated[index].demand_uom && !validUoms.includes(updated[index].demand_uom)) {
        updated[index].demand_uom = validUoms[0];
      }
    }
    setMeterReads(updated);
  };

  const removeMeterRead = (index) => {
    if (window.confirm('Remove this meter reading?')) {
      setMeterReads(meterReads.filter((_, i) => i !== index));
    }
  };

  // Usage detail handlers
  const addUsageDetail = () => {
    setUsageDetails([...usageDetails, {
      child_account_no: '',
      meter_number: '',
      commodity: 'Electricity',
      usage_type: 'Supply',
      line_item_description: '',
      usage_quantity: '0',
      uom: 'kWh',
      rate: '0',
      line_item_charge: '0'
    }]);
  };

  const updateUsageDetail = (index, field, value) => {
    const updated = [...usageDetails];
    updated[index] = { ...updated[index], [field]: value };
    if (field === 'commodity') {
      const validUoms = uomOptionsFor(value);
      if (!validUoms.includes(updated[index].uom)) {
        updated[index].uom = validUoms[0];
      }
    }
    setUsageDetails(updated);
  };

  const removeUsageDetail = (index) => {
    if (window.confirm('Remove this usage item?')) {
      setUsageDetails(usageDetails.filter((_, i) => i !== index));
    }
  };

  // Other Charges helpers
  const addOtherCharge = () => {
    setOtherCharges([...otherCharges, {
      child_account_no: '',
      line_item_description: '',
      usage: '',
      rate: '',
      charge: '0',
      page_number: null,
    }]);
  };
  const updateOtherCharge = (index, field, value) => {
    const updated = [...otherCharges];
    updated[index] = { ...updated[index], [field]: value };
    setOtherCharges(updated);
  };
  const removeOtherCharge = (index) => {
    if (window.confirm('Remove this other charge?')) {
      setOtherCharges(otherCharges.filter((_, i) => i !== index));
    }
  };

  // Bill-level validation memo (mirrors single-Viewer intelligentValidation
  // in a lightweight way for the consolidated Validation tab).
  const parseAmount = (v) => {
    if (v === null || v === undefined) return 0;
    const s = String(v).replace(/[$,]/g, '').trim();
    if (!s || ['N/A', 'None', 'null', '-', '—'].includes(s)) return 0;
    const n = parseFloat(s);
    return Number.isNaN(n) ? 0 : n;
  };
  const consValidation = (() => {
    const totalCharges = parseAmount(statement.total_charges) || parseAmount(statement.new_charges);
    const totalAmountDue = parseAmount(statement.total_amount_due);
    const previousBalance = parseAmount(statement.previous_balance);
    const paymentsReceived = Math.abs(parseAmount(statement.payments_received));
    const credits = Math.abs(parseAmount(statement.credits));
    const explicitAdj = parseAmount(statement.adjustments);
    const lateFee = parseAmount(statement.late_fee);
    const printedBBF = parseAmount(statement.balance_forward);
    const tol = Math.max(1, totalAmountDue * 0.01);

    // ---- Deterministic Balance-Forward / Adjustment reconciliation ----
    // True Balance Forward = Previous Balance − Payments Received. Many bills
    // print NO Balance Forward line; the parser then INFERS one (often
    // total − current) which mislabels a real Adjustment as a Balance Forward and
    // DOUBLE-COUNTS once the Adjustments line is also captured. When prev/payments
    // are known and the printed/inferred BBF disagrees with (prev − payments),
    // restore the TRUE BBF and move the difference into Adjustments (unless a
    // captured adjustment already closes the total). RELABEL-only — it can never
    // mask a genuine total mismatch; it just stops a derived BBF being wrong.
    const calculatedBBF = previousBalance - paymentsReceived;
    const tol2 = Math.max(1, totalAmountDue * 0.02);
    const havePrevPay = previousBalance !== 0 || paymentsReceived !== 0;

    let balanceForward = printedBBF;
    let adjustments = explicitAdj;
    let bbfIsCalculated = false;   // true when BBF is derived (no reliable printed BBF)
    if (havePrevPay && Math.abs(printedBBF - calculatedBBF) > tol2) {
      balanceForward = calculatedBBF;
      bbfIsCalculated = true;
      const closesWithExplicit = Math.abs(calculatedBBF + totalCharges + lateFee + explicitAdj - credits - totalAmountDue) <= tol2;
      if (!closesWithExplicit) {
        // the excess folded into balance_forward IS the adjustment
        adjustments = Math.round((printedBBF - calculatedBBF) * 100) / 100;
      }
    }

    // Total Amount Due = BBF + Current + Late + Adjustments − Credits.
    let totalDueFormula = balanceForward + totalCharges + lateFee + adjustments - credits;
    let formulaDiff = Math.abs(totalDueFormula - totalAmountDue);
    // Sign fallback: if it doesn't reconcile and the adjustment sign is ambiguous,
    // try flipping the adjustment and keep it only if it reconciles better.
    if (formulaDiff > tol && Math.abs(adjustments) > tol) {
      const flipped = balanceForward + totalCharges + lateFee - adjustments - credits;
      if (Math.abs(flipped - totalAmountDue) < formulaDiff) {
        adjustments = -adjustments;
        totalDueFormula = flipped;
        formulaDiff = Math.abs(flipped - totalAmountDue);
      }
    }

    // Child sums from the fields the extractor ACTUALLY emits.
    const childSum = childInvoices.reduce((s, c) => s + parseAmount(c.new_charges), 0);
    const childDueSum = childInvoices.reduce((s, c) => s + parseAmount(c.total_amount_due), 0);
    const otherSum = otherCharges.reduce((s, o) => s + parseAmount(o.charge), 0);
    const chargesPlusOther = totalCharges + otherSum;
    // BBF check: does the PRINTED balance_forward agree with prev − payments?
    // N/A when there is no printed BBF or we derived it (bbfIsCalculated).
    const bbfCandidates = [calculatedBBF, calculatedBBF - credits];
    const bbfMatches = (previousBalance === 0 && paymentsReceived === 0) || printedBBF === 0 || bbfIsCalculated
      ? null
      : bbfCandidates.some((v) => Math.abs(printedBBF - v) <= Math.max(0.5, Math.abs(printedBBF) * 0.01));
    return {
      totalCharges,
      totalAmountDue,
      previousBalance,
      paymentsReceived,
      credits,
      adjustments,
      lateFee,
      printedBBF,
      calculatedBBF,
      balanceForward,
      bbfIsCalculated,
      bbfMatches,
      childSum,
      childDueSum,
      otherSum,
      chargesPlusOther,
      totalDueFormula,
      formulaDiff,
      childSumMatches: totalCharges > 0 && Math.abs(childSum - totalCharges) <= Math.max(1, totalCharges * 0.02),
      // Total due reconciles ONLY when the balance equation itself closes. The
      // Σ-of-accounts cross-check is reported SEPARATELY (childDueMatches) — it
      // must never paint a broken formula green.
      totalDueMatches: totalAmountDue > 0 && formulaDiff <= tol,
      childDueMatches: childDueSum > 0 && Math.abs(childDueSum - totalAmountDue) <= tol,
      statementEmpty: totalCharges === 0 && totalAmountDue === 0 && childInvoices.length > 0,
    };
  })();

  // Per-account reconciliation (LIVE — respects in-progress edits):
  // for each unique child account, sum its usage/other line items and compare
  // against the child invoice's new_charges. Handles dual field names
  // (charges|line_item_charge, charge|amount) and duplicate account entries.
  const childReconciliation = (() => {
    const normA = (a) => String(a || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    const pickAmt = (row, primary, fallback) => {
      const p = row[primary];
      if (p !== undefined && p !== null && p !== '') return parseAmount(p);
      return parseAmount(row[fallback]);
    };
    const groups = new Map();
    childInvoices.forEach((c) => {
      const k = normA(c.child_account_no);
      if (!k) return;
      if (!groups.has(k)) {
        groups.set(k, { account: c.child_account_no, address: c.unit_address, count: 0, newCharges: 0, lateFee: 0, credits: 0, adjustments: 0, totalDue: 0, prevBalance: 0, payments: 0 });
      }
      const g = groups.get(k);
      g.count += 1;
      g.newCharges += parseAmount(c.new_charges);
      g.lateFee += parseAmount(c.late_fee);
      g.credits += Math.abs(parseAmount(c.credits));
      g.adjustments += parseAmount(c.adjustments);
      g.totalDue += parseAmount(c.total_amount_due);
      g.prevBalance += parseAmount(c.previous_balance);
      g.payments += Math.abs(parseAmount(c.payments_received));
    });
    const rows = [];
    const matrixByAcct = new Map(
      ((consolidatedValidation && consolidatedValidation.account_summary_matrix) || []).map((r) => [normA(r.account_no), r])
    );
    groups.forEach((g, k) => {
      const uRows = usageDetails.filter((u) => normA(u.child_account_no) === k);
      const oRows = otherCharges.filter((o) => normA(o.child_account_no) === k);
      const usum = uRows.reduce((s, u) => s + pickAmt(u, 'charges', 'line_item_charge'), 0);
      const osum = oRows.reduce((s, o) => s + pickAmt(o, 'charge', 'amount'), 0);
      const lineItemCount = uRows.length + oRows.length;
      const calc = usum + osum + g.lateFee;
      const calcNoLate = usum + osum;   // bills that EXCLUDE late fee from new_charges
      const tol = Math.max(0.5, Math.abs(g.newCharges) * 0.01);
      // Reconcile against whichever variant is closer to New Charges — some bills
      // fold the late fee into new_charges, others don't; and the parser sometimes
      // DUPLICATES an adjustment into late_fee, which must NOT read as a line-item
      // shortfall. We show the reconciling sum so the Diff reflects the true
      // Current-Charges-vs-line-items gap (not the adjustment).
      const bestCalc = Math.abs(g.newCharges - calcNoLate) <= Math.abs(g.newCharges - calc) ? calcNoLate : calc;
      const diff = g.newCharges - bestCalc;
      // Line items may include an adjustment/credit line that new_charges
      // excludes — accept those variants (mirrors Lambda validation). The
      // adjustment often lives on the summary-table row, not the child.
      const mrow = matrixByAcct.get(k);
      const cr = g.credits || Math.abs(parseAmount(mrow && mrow.summary_credits));
      const adj = g.adjustments || parseAmount(mrow && mrow.summary_adjustments);
      const adjOk = (adj !== 0 || cr !== 0) && [
        calc - adj, calc + adj, calc + cr, calc - adj + cr,
      ].some((alt) => Math.abs(g.newCharges - alt) <= tol);
      // Per-account TOTAL consistency: New Charges + Adjustment(±) + BBF == Total
      // Amount Due. The adjustment (Total − New − BBF) is shown explicitly so it
      // is never silently swallowed. BBF = Previous Balance − Payments (usually 0).
      const childBBF = g.prevBalance - g.payments;
      const impliedAdj = g.totalDue > 0 ? (g.totalDue - g.newCharges - childBBF) : 0;
      const ttol = Math.max(0.5, Math.abs(g.totalDue) * 0.01);
      // If the account carries a captured adjustment, verify it (either sign)
      // closes the total; otherwise the implied adjustment IS the reconciling
      // amount (informational — flagged only when there is a total but it cannot
      // be explained by new charges alone and no adjustment/BBF accounts for it).
      const totalOk = g.totalDue === 0
        || Math.abs(g.newCharges + childBBF - g.totalDue) <= ttol
        || Math.abs(g.newCharges + childBBF + Math.abs(adj) - g.totalDue) <= ttol
        || Math.abs(g.newCharges + childBBF - Math.abs(adj) - g.totalDue) <= ttol
        || (adj === 0 && Math.abs(impliedAdj) > ttol);
      rows.push({
        ...g,
        usageSum: usum,
        otherSum: osum,
        lineItemSum: bestCalc,
        diff,
        impliedAdj,
        childBBF,
        totalOk,
        // A genuinely reconciled $0 account (no new charges AND nothing due —
        // e.g. a terminated site whose only charge is a fully-waived fee that
        // nets to zero) is VALID per spec ("$0 total due must be valid"). It
        // must never be flagged as failing or as needing AI recovery.
        validZero: g.newCharges === 0 && g.totalDue === 0,
        // hasLines reflects the PRESENCE of line-item rows, not their net sum —
        // a fee + its exact offset (net $0) still counts as having line items.
        hasLines: lineItemCount > 0 || usum !== 0 || osum !== 0,
        ok: (g.newCharges === 0 && g.totalDue === 0)
          || (g.newCharges !== 0 && (Math.abs(diff) <= tol || adjOk)),
      });
    });
    const bad = rows.filter((r) => r.hasLines && !r.ok && !r.validZero).length;
    const dups = rows.filter((r) => r.count > 1);
    return { rows, bad, dups };
  })();

  // Summary-table ↔ child-pages matrix from the Lambda validation payload.
  const summaryMatrix = (consolidatedValidation && consolidatedValidation.account_summary_matrix) || [];

  // Meter completeness (INFORMATIONAL — never blocks approval). Per account:
  // expected meters = number of distinct commodities present (a bill with N
  // commodities should have >= N meters); flag when extracted < expected.
  // Recomputed live from the current meter/usage rows so it tracks edits.
  const meterCompleteness = (() => {
    const canon = (v) => {
      const s = String(v || '').toLowerCase().replace(/[^a-z ]/g, '').trim();
      if (!s) return '';
      if (/elec|power|kwh/.test(s)) return 'electric';
      if (/gas|therm/.test(s)) return 'gas';
      if (/sewer|wastewater/.test(s)) return 'sewer';
      if (/irrigat|reclaim/.test(s)) return 'irrigation';
      if (/storm/.test(s)) return 'stormwater';
      if (/water/.test(s)) return 'water';
      return s.replace(/\s+/g, '');
    };
    const ak = (a) => String(a || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    const groups = new Map();
    (meterReads || []).forEach((m) => {
      const k = ak(m.child_account_no);
      if (!k) return;
      if (!groups.has(k)) groups.set(k, { account: m.child_account_no, meters: 0, commodities: new Set() });
      const g = groups.get(k);
      g.meters += 1;
      const c = canon(m.commodity);
      if (c) g.commodities.add(c);
    });
    (usageDetails || []).forEach((u) => {
      const k = ak(u.child_account_no);
      if (!k) return;
      if (!groups.has(k)) groups.set(k, { account: u.child_account_no, meters: 0, commodities: new Set() });
      const c = canon(u.commodity || u.usage_type);
      if (c) groups.get(k).commodities.add(c);
    });
    const rows = [];
    let flagged = 0; let expTot = 0; let extTot = 0;
    groups.forEach((g) => {
      const commodities = Array.from(g.commodities);
      const expected = commodities.length;
      const extracted = g.meters;
      const ok = extracted >= expected;
      expTot += expected; extTot += extracted;
      if (!ok) flagged += 1;
      rows.push({ account: g.account, commodities, expected, extracted, ok });
    });
    return { rows, flagged, expectedTotal: expTot, extractedTotal: extTot, ok: flagged === 0, accounts: rows.length };
  })();

  // Validation-tab accordion auto-open set — the checks that currently FAIL.
  // Recomputed every render from the live validation results.
  const autoOpenValidationItems = [
    !consValidation.totalDueMatches && 'total',
    consValidation.bbfMatches === false && 'bbf',
    summaryMatrix.some((r) => r.status === 'mismatch' || r.status === 'missing_child') && 'summary',
    childReconciliation.bad > 0 || childReconciliation.rows.some((r) => !r.hasLines) ? 'lineitem' : null,
    meterCompleteness.flagged > 0 && 'meter',
    (consolidatedValidation?.horizontal_line_checks?.failed > 0) && 'horizontal',
  ].filter(Boolean);
  const validationAccordionValue = validationAccordion === null ? autoOpenValidationItems : validationAccordion;

  // Revalidate — re-run all validation checks from the ALREADY-parsed data.
  // Calls the backend restamp endpoint (no OCR, no LLM, no credits), then
  // re-fetches the document so every client-side check + persisted status is
  // refreshed, and resets the accordion so failing checks re-expand.
  const handleRevalidate = async () => {
    if (!currentAccount) return;
    setRevalidating(true);
    try {
      await api.post(`/accounts/${currentAccount.id}/documents/${documentId}/revalidate`);
      await fetchDocument();
      setValidationAccordion(null);
      toast.success('Validation refreshed — no AI credits used.');
    } catch (e) {
      console.error('Revalidate failed:', e);
      toast.error(e?.response?.data?.detail || 'Could not refresh validation.');
    } finally {
      setRevalidating(false);
    }
  };

  // Accounts that need targeted AI recovery (no line items, or don't reconcile).
  const recoverNoLine = childReconciliation.rows.filter((r) => !r.hasLines && !r.validZero).length;
  const recoverOffBy = childReconciliation.bad;
  const recoverTotal = recoverNoLine + recoverOffBy;
  // Accounts missing meter reads — a deep sweep can backfill them.
  const accountsMissingMeters = childInvoices.filter((c) => !((c.meter_reads || []).length)).length;
  const showRecover = recoverTotal > 0 || accountsMissingMeters > 0;

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const renderMissing = (fieldKey) => (
    mandatoryFieldsValidation.missingKeys.has(fieldKey)
      ? (
        <span
          title="Missing / did not parse — please fill in"
          data-testid={`cons-missing-icon-${fieldKey}`}
          className="inline-flex items-center ml-1"
          key={`cons-missing-${fieldKey}`}
        >
          <AlertTriangle className="h-3.5 w-3.5 text-red-600" />
        </span>
      )
      : null
  );

  return (
    <div className="h-screen flex flex-col bg-background">
      {reparseConfirmDialog}
      {/* Header */}
      <div className="border-b border-border bg-card px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/bill-jobs')}
            className="rounded-none"
            data-testid="back-to-bills"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Intake Hub
          </Button>
          
          <div className="flex items-center gap-2">
            <Layers className="h-5 w-5 text-purple-600" />
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-lg font-bold tracking-tight">{document?.filename}</h1>
                {hasBillPortalExport && (
                  <Badge variant="outline" className="rounded-none text-[10px] px-1 py-0 bg-emerald-50 text-emerald-700 border-emerald-200">
                    BP
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="rounded-none text-xs bg-purple-50 text-purple-700 border-purple-200">
                  Consolidated Bill
                </Badge>
                <Badge variant="secondary" className="rounded-none text-xs">
                  <FileStack className="h-3 w-3 mr-1" />
                  {childInvoices.length} Bills / Accounts
                </Badge>
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Download PDF */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (pdfUrl) {
                      const link = window.document.createElement('a');
                      link.href = pdfUrl;
                      link.download = document?.filename || 'bill.pdf';
                      link.click();
                    }
                  }}
                  disabled={!pdfUrl}
                  className="rounded-none"
                  data-testid="download-bill"
                >
                  <Download className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-xs">Download PDF</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          
          {/* Download JSON */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadJSON}
                  disabled={document?.status !== 'completed'}
                  className="rounded-none"
                  data-testid="download-json"
                >
                  <FileText className="h-4 w-4" />
                  <span className="ml-1 text-xs">JSON</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-xs">{hasBillPortalExport ? 'Download BillPortal JSON' : 'Convert & Download JSON'}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                disabled={reparsing || document?.status === 'processing'}
                className="rounded-none"
                data-testid="cons-reparse-button"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${reparsing || document?.status === 'processing' ? 'animate-spin' : ''}`} />
                {reparsing ? 'Re-parsing...' : document?.status === 'processing' ? 'Processing...' : 'Re-parse'}
                <ChevronDown className="h-4 w-4 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="rounded-none">
              <DropdownMenuItem onClick={() => handleReparse('all')} className="cursor-pointer">
                <RefreshCw className="h-4 w-4 mr-2" />
                Re-parse All Fields
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleReparse('missing')} className="cursor-pointer">
                <AlertCircle className="h-4 w-4 mr-2" />
                Fill Missing Fields Only
              </DropdownMenuItem>
              <ReparseTypeItems onPick={(t) => handleReparse('all', t)} testIdPrefix="cons-reparse-as" />
            </DropdownMenuContent>
          </DropdownMenu>
          
          {/* Auto recover — targeted AI re-read of failing accounts + meter-read backfill */}
          {showRecover && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRecover}
                    disabled={recovering}
                    className="rounded-none border-amber-400 text-amber-700 hover:bg-amber-50"
                    data-testid="cons-recover-btn"
                  >
                    {recovering
                      ? <Loader2 className="h-4 w-4 animate-spin" />
                      : <Wand2 className="h-4 w-4" />}
                    <span className="ml-1 text-xs">{recovering ? 'Recovering…' : 'Auto recover'}</span>
                    {!recovering && recoverTotal > 0 && (
                      <Badge className="ml-1 rounded-none text-[10px] px-1 py-0 bg-amber-100 text-amber-800 border border-amber-300 hover:bg-amber-100">
                        {recoverTotal}
                      </Badge>
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs">
                  <p className="text-xs">
                    AI re-reads each account from its detail page(s) — including headerless
                    continuation pages — from the stored bill text (no full re-parse).
                    {recoverTotal > 0
                      ? ` Fixes the ${recoverTotal} account${recoverTotal > 1 ? 's' : ''} that ${recoverTotal > 1 ? "don't" : "doesn't"} reconcile`
                      : ' Re-verifies all accounts'}
                    {accountsMissingMeters > 0
                      ? ` and backfills meter reads for ${accountsMissingMeters} account${accountsMissingMeters > 1 ? 's' : ''}.`
                      : ' and refreshes meter reads.'}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

          {/* Audit Trail Button */}
          <Button
            variant="outline"
            size="sm"
            className="rounded-none"
            onClick={() => { fetchAuditLogs(); setShowAuditModal(true); }}
          >
            <History className="h-4 w-4 mr-2" />
            History
          </Button>
          
          {editMode ? (
            <>
              <Button
                variant="ghost"
                size="sm"
                data-testid="cons-cancel-edit-btn"
                onClick={() => {
                  setEditMode(false);
                  fetchDocument();
                }}
                className="rounded-none"
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSave}
                disabled={saving}
                className="rounded-none font-bold"
              >
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save Changes
              </Button>
            </>
          ) : (
            <Button
              size="sm"
              onClick={async () => {
                if (reviewStatus === 'approved') { await handleStatusChange('review'); }
                setEditMode(true);
              }}
              className="rounded-none font-bold"
              data-testid="cons-edit-unlock-btn"
              title={reviewStatus === 'approved' ? 'This bill is Validated & Locked. Click to unlock and edit — it will re-validate on save.' : 'Edit this bill'}
            >
              {reviewStatus === 'approved' ? (
                <Lock className="h-4 w-4 mr-2" />
              ) : (
                <Edit className="h-4 w-4 mr-2" />
              )}
              {reviewStatus === 'approved' ? 'Unlock & Edit' : 'Edit'}
            </Button>
          )}
        </div>
      </div>
      
      {/* Workflow Bar */}
      <div className="border-b border-border bg-card px-6 py-2 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Label className="text-xs uppercase font-medium">Assigned:</Label>
            <Select value={assignedTo || '__unassigned__'} onValueChange={(value) => handleAssignment(value === '__unassigned__' ? null : value)}>
              <SelectTrigger className="w-40 h-8 rounded-none text-xs">
                <SelectValue placeholder="Unassigned" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__unassigned__">Unassigned</SelectItem>
                {accountUsers.map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {(() => {
          const noLine = childReconciliation.rows.filter((r) => !r.hasLines && !r.validZero).length;
          const failing = childReconciliation.bad > 0 || noLine > 0;
          return failing ? (
            <Badge variant="destructive" className="rounded-none bg-amber-500 hover:bg-amber-500" data-testid="cons-validation-status-badge">
              <AlertTriangle className="h-3 w-3 mr-1" />
              Validation Failed ({noLine + childReconciliation.bad} account{(noLine + childReconciliation.bad) > 1 ? 's' : ''})
            </Badge>
          ) : reviewStatus === 'approved' ? (
            <Badge className="rounded-none bg-emerald-600" data-testid="cons-validation-status-badge">
              <Lock className="h-3 w-3 mr-1" />
              Validated &amp; Locked
            </Badge>
          ) : (
            <Badge variant="outline" className="rounded-none" data-testid="cons-validation-status-badge">
              <Clock className="h-3 w-3 mr-1" />
              Validating
            </Badge>
          );
        })()}
      </div>
      
      {/* Main Content */}
      <ResizablePanelGroup direction="horizontal" className="flex-1">
        {/* PDF Viewer */}
        <ResizablePanel defaultSize={50} minSize={30}>
          <div className="h-full flex flex-col bg-muted/30">
            {/* PDF Controls */}
            <div className="border-b border-border bg-card px-4 py-2 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={handleZoomOut} className="rounded-none h-8 w-8 p-0">
                  <ZoomOut className="h-4 w-4" />
                </Button>
                <span className="text-sm font-mono w-16 text-center">{Math.round(scale * 100)}%</span>
                <Button variant="outline" size="sm" onClick={handleZoomIn} className="rounded-none h-8 w-8 p-0">
                  <ZoomIn className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={handleZoomReset} className="rounded-none text-xs">
                  Reset
                </Button>
              </div>
              {numPages && (
                <span className="text-sm text-muted-foreground" data-testid="pdf-page-indicator">
                  Page {currentPage} of {numPages}
                </span>
              )}
            </div>
            
            {/* PDF Document */}
            <div className="flex-1 overflow-auto p-4" ref={pdfContainerRef}>
              {pdfUrl ? (
                <Document
                  file={pdfUrl}
                  onLoadSuccess={onDocumentLoadSuccess}
                  options={pdfOptions}
                  loading={<Loader2 className="h-8 w-8 animate-spin mx-auto" />}
                  className="flex flex-col items-center gap-4"
                >
                  {Array.from(new Array(numPages), (el, index) => (
                    <LazyPdfPage
                      key={`page_${index + 1}`}
                      pageNumber={index + 1}
                      scale={scale}
                      className="shadow-lg transition-all"
                      isActive={currentPage === index + 1}
                      estimatedHeight={792}
                      onRenderError={(error) => console.warn('Page render error:', error)}
                    />
                  ))}
                </Document>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              )}
            </div>
          </div>
        </ResizablePanel>
        
        <ResizableHandle withHandle />
        
        {/* Data Panel */}
        <ResizablePanel defaultSize={50} minSize={30}>
          <div className="h-full flex flex-col overflow-hidden">
            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
              <div className="border-b border-border bg-card px-4">
                <TabsList className="rounded-none h-10 p-0 bg-transparent">
                  <TabsTrigger value="statement" className="rounded-none data-[state=active]:bg-background data-[state=active]:shadow-sm px-4">
                    Statement
                  </TabsTrigger>
                  <TabsTrigger value="summary" className="rounded-none data-[state=active]:bg-background data-[state=active]:shadow-sm px-4">
                    Summary ({childInvoices.length})
                  </TabsTrigger>
                  <TabsTrigger value="meter" className="rounded-none data-[state=active]:bg-background data-[state=active]:shadow-sm px-4">
                    Meter ({uniqueMeterCount})
                  </TabsTrigger>
                  <TabsTrigger value="usage" className="rounded-none data-[state=active]:bg-background data-[state=active]:shadow-sm px-4">
                    Usage ({uniqueUsageCount})
                  </TabsTrigger>
                  <TabsTrigger value="other" className="rounded-none data-[state=active]:bg-background data-[state=active]:shadow-sm px-4" data-testid="cons-tab-other">
                    Other ({otherCharges.length})
                  </TabsTrigger>
                  <TabsTrigger value="validation" className="rounded-none data-[state=active]:bg-background data-[state=active]:shadow-sm px-4" data-testid="cons-tab-validation">
                    <span className="flex items-center gap-1">
                      Validation
                      {(!mandatoryFieldsValidation.allPresent || !consValidation.totalDueMatches || childReconciliation.bad > 0) ? (
                        <AlertTriangle className="h-3 w-3 text-red-600" data-testid="cons-validation-warning" />
                      ) : (
                        <Check className="h-3 w-3 text-green-600" data-testid="cons-validation-ok" />
                      )}
                    </span>
                  </TabsTrigger>
                </TabsList>
              </div>
              
              <div className="flex-1 overflow-auto">
                {/* Statement Tab - Unified layout matching Single Bill viewer */}
                <TabsContent value="statement" className="m-0 p-6 h-full overflow-auto">
                  <h3 className="text-lg font-bold mb-4">Bill Statement</h3>
                  
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    {/* Row 1: Property/Vendor Info */}
                    <div>
                      <Label className="text-xs uppercase tracking-wide font-medium inline-flex items-center gap-1">
                        Property Name
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="inline-flex items-center cursor-help" data-testid="cons-property-name-info">
                                <Info className="h-3.5 w-3.5 text-muted-foreground" />
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs">
                              <p className="text-xs">Property Name is auto-assigned: if the property is onboarded, it's filled from onboarding data based on the assigned vendor account number. If not, it uses the property name mapped to the parsed service address.</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </Label>
                      <Input
                        value={statement.property_name || ''}
                        onChange={(e) => setStatement({ ...statement, property_name: e.target.value })}
                        disabled={!editMode}
                        className="rounded-none mt-1 font-mono text-sm"
                        data-testid="cons-statement-property-name"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs uppercase tracking-wide font-medium">Vendor Name{renderMissing("vendor_name")}</Label>
                      <Input
                        value={statement.vendor_name || ''}
                        onChange={(e) => setStatement({ ...statement, vendor_name: e.target.value })}
                        disabled={!editMode}
                        className="rounded-none mt-1 font-mono text-sm"
                        data-testid="cons-statement-vendor-name"
                      />
                    </div>
                    
                    {/* Row 2: Account Info */}
                    <div>
                      <Label className="text-xs uppercase tracking-wide font-medium">Parent Account No{renderMissing("parent_account_no")}</Label>
                      <Input
                        value={statement.parent_account_no || ''}
                        onChange={(e) => setStatement({ ...statement, parent_account_no: e.target.value })}
                        disabled={!editMode}
                        className="rounded-none mt-1 font-mono text-sm"
                        data-testid="cons-statement-parent-account"
                      />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wide font-medium">Master Account No</Label>
                      <Input
                        value={statement.master_account_no || ''}
                        onChange={(e) => setStatement({ ...statement, master_account_no: e.target.value })}
                        disabled={!editMode}
                        className="rounded-none mt-1 font-mono text-sm"
                        data-testid="cons-statement-master-account"
                      />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wide font-medium">Billed Customer Name{renderMissing("billed_customer_name")}</Label>
                      <Input
                        value={statement.billed_customer_name || ''}
                        onChange={(e) => setStatement({ ...statement, billed_customer_name: e.target.value })}
                        disabled={!editMode}
                        className="rounded-none mt-1 font-mono text-sm"
                        data-testid="cons-statement-billed-customer"
                      />
                    </div>
                    
                    {/* Row 3: Address */}
                    <div className="col-span-2">
                      <Label className="text-xs uppercase tracking-wide font-medium">Billing Address</Label>
                      <Input
                        value={statement.billed_address || ''}
                        onChange={(e) => setStatement({ ...statement, billed_address: e.target.value })}
                        disabled={!editMode}
                        className="rounded-none mt-1 font-mono text-sm"
                        data-testid="cons-statement-billing-address"
                      />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wide font-medium">City</Label>
                      <Input
                        value={statement.city || ''}
                        onChange={(e) => setStatement({ ...statement, city: e.target.value })}
                        disabled={!editMode}
                        className="rounded-none mt-1 font-mono text-sm"
                      />
                    </div>
                    
                    {/* Row 4: State/Zip/Invoice */}
                    <div>
                      <Label className="text-xs uppercase tracking-wide font-medium">State</Label>
                      <Input
                        value={statement.state || ''}
                        onChange={(e) => setStatement({ ...statement, state: e.target.value })}
                        disabled={!editMode}
                        className="rounded-none mt-1 font-mono text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wide font-medium">Postcode</Label>
                      <Input
                        value={statement.postcode || ''}
                        onChange={(e) => setStatement({ ...statement, postcode: e.target.value })}
                        disabled={!editMode}
                        className="rounded-none mt-1 font-mono text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wide font-medium">Invoice No</Label>
                      <Input
                        value={statement.invoice_no || ''}
                        onChange={handleInvoiceNoChange}
                        disabled={!editMode}
                        data-testid="cons-statement-invoice-no"
                        className="rounded-none mt-1 font-mono text-sm"
                      />
                    </div>
                    
                    {/* Row 5: Dates */}
                    <div>
                      <Label className="text-xs uppercase tracking-wide font-medium">Issue Date{renderMissing("issue_date")}</Label>
                      <Input
                        type="date"
                        value={statement.issue_date || ''}
                        onChange={(e) => setStatement({ ...statement, issue_date: e.target.value })}
                        disabled={!editMode}
                        className="rounded-none mt-1 font-mono text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wide font-medium">Period From</Label>
                      <Input
                        type="date"
                        value={statement.period_from || ''}
                        onChange={(e) => setStatement({ ...statement, period_from: e.target.value })}
                        disabled={!editMode}
                        className="rounded-none mt-1 font-mono text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wide font-medium">Period To{renderMissing("period_to")}</Label>
                      <Input
                        type="date"
                        value={statement.period_to || ''}
                        onChange={(e) => setStatement({ ...statement, period_to: e.target.value })}
                        disabled={!editMode}
                        className="rounded-none mt-1 font-mono text-sm"
                      />
                    </div>
                    
                    {/* Row 6: Due Date & Financial */}
                    <div>
                      <Label className="text-xs uppercase tracking-wide font-medium">Due Date</Label>
                      <Input
                        type="date"
                        value={statement.due_date || ''}
                        onChange={(e) => setStatement({ ...statement, due_date: e.target.value })}
                        disabled={!editMode}
                        min={statement.issue_date || undefined}
                        className={`rounded-none mt-1 font-mono text-sm ${dueDateError ? 'border-red-500' : ''}`}
                        data-testid="cons-statement-due-date"
                      />
                      {dueDateError && (
                        <p className="text-xs text-red-600 mt-1" data-testid="cons-due-date-error">{dueDateError}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wide font-medium">Previous Balance</Label>
                      <Input
                        inputMode="decimal"
                        value={statement.previous_balance || '0'}
                        onChange={handleAmountChange('previous_balance')}
                        disabled={!editMode}
                        className="rounded-none mt-1 font-mono text-sm"
                        data-testid="cons-statement-previous-balance"
                      />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wide font-medium">Payments Received</Label>
                      <Input
                        inputMode="decimal"
                        value={statement.payments_received || '0'}
                        onChange={handleAmountChange('payments_received')}
                        disabled={!editMode}
                        className="rounded-none mt-1 font-mono text-sm"
                        data-testid="cons-statement-payments-received"
                      />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wide font-medium">Balance Forward</Label>
                      <Input
                        inputMode="decimal"
                        value={statement.balance_forward || '0'}
                        onChange={handleAmountChange('balance_forward')}
                        disabled={!editMode}
                        className="rounded-none mt-1 font-mono text-sm"
                        data-testid="cons-statement-balance-forward"
                      />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wide font-medium">Late Fee</Label>
                      <Input
                        inputMode="decimal"
                        value={statement.late_fee || '0'}
                        onChange={handleAmountChange('late_fee')}
                        disabled={!editMode}
                        className="rounded-none mt-1 font-mono text-sm"
                        data-testid="cons-statement-late-fee"
                      />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wide font-medium">Credits</Label>
                      <Input
                        inputMode="decimal"
                        value={statement.credits || '0'}
                        onChange={handleAmountChange('credits')}
                        disabled={!editMode}
                        className="rounded-none mt-1 font-mono text-sm"
                        data-testid="cons-statement-credits"
                      />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wide font-medium">Adjustments</Label>
                      <Input
                        inputMode="decimal"
                        value={statement.adjustments || '0'}
                        onChange={handleAmountChange('adjustments')}
                        disabled={!editMode}
                        className="rounded-none mt-1 font-mono text-sm"
                        data-testid="cons-statement-adjustments"
                      />
                    </div>
                  </div>
                  
                  {/* Financial Summary with Validation */}
                  <div className="border-t border-border pt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs uppercase tracking-wide font-medium flex items-center gap-2">
                          Current Charges (New Charges)
                          {renderMissing("total_charges")}
                          {totalValidation.hasData && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="inline-flex items-center">
                                    {totalValidation.isValid ? (
                                      <CheckCircle className="h-4 w-4 text-green-500" />
                                    ) : (
                                      <AlertTriangle className="h-4 w-4 text-amber-500" />
                                    )}
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="max-w-xs">
                                  {totalValidation.isValid ? (
                                    <p className="text-xs">
                                      <span className="font-semibold text-green-600">Validated ({totalValidation.basis})</span><br/>
                                      Σ accounts: ${totalValidation.sumChildTotal}<br/>
                                      Bill: ${totalValidation.statementTotal}
                                    </p>
                                  ) : (
                                    <p className="text-xs">
                                      <span className="font-semibold text-amber-600">{totalValidation.basis} variance: ${totalValidation.variance}</span><br/>
                                      Σ accounts: ${totalValidation.sumChildTotal}<br/>
                                      Bill: ${totalValidation.statementTotal}
                                    </p>
                                  )}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </Label>
                        <Input
                          inputMode="decimal"
                          value={statement.total_charges || '0'}
                          onChange={handleAmountChange('total_charges')}
                          disabled={!editMode}
                          className={`rounded-none mt-1 font-mono text-sm ${totalValidation.hasData && !totalValidation.isValid ? 'border-amber-400' : ''}`}
                          data-testid="cons-statement-total-charges"
                        />
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-wide font-medium">Total Amount Due{renderMissing("total_amount_due")}</Label>
                        <Input
                          inputMode="decimal"
                          value={statement.total_amount_due || '0'}
                          onChange={handleAmountChange('total_amount_due')}
                          disabled={!editMode}
                          className="rounded-none mt-1 font-mono text-sm"
                          data-testid="cons-statement-total-amount-due"
                        />
                      </div>
                    </div>
                    
                    {/* Validation Summary Card */}
                    {totalValidation.hasData && (
                      <div className={`mt-4 p-3 border rounded-none ${totalValidation.isValid ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'}`}>
                        <div className="flex items-center gap-2">
                          {totalValidation.isValid ? (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          ) : (
                            <AlertTriangle className="h-4 w-4 text-amber-600" />
                          )}
                          <span className={`text-sm ${totalValidation.isValid ? 'text-green-700' : 'text-amber-700'}`}>
                            {totalValidation.isValid 
                              ? `Total Check: Σ accounts' ${totalValidation.basis} ($${totalValidation.sumChildTotal}) matches the bill ($${totalValidation.statementTotal})` 
                              : `${totalValidation.basis} variance: bill $${totalValidation.statementTotal} vs Σ accounts $${totalValidation.sumChildTotal} (Δ $${totalValidation.variance})`}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </TabsContent>
                
                {/* Summary Tab - Child Invoices */}
                <TabsContent value="summary" className="m-0 h-full overflow-hidden">
                  <div className="h-full flex flex-col p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-sm font-bold uppercase tracking-wide text-muted-foreground">Child Invoice Summary</h3>
                      {editMode && (
                        <Button size="sm" onClick={addChildInvoice} className="rounded-none">
                          <Plus className="h-4 w-4 mr-1" /> Add Invoice
                        </Button>
                      )}
                    </div>
                    
                    {childInvoices.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        No child invoices found
                      </div>
                    ) : (
                      <div className="flex-1 overflow-auto border border-border">
                        <table className="text-sm" style={{ minWidth: '2100px' }}>
                          <thead className="bg-muted/50 sticky top-0">
                            <tr>
                              <th className="text-center p-2 text-xs font-medium border-b whitespace-nowrap" style={{ minWidth: '50px' }}>Page</th>
                              <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap" style={{ minWidth: '150px' }}>
                                <div className="flex items-center gap-1">
                                  Customer
                                  {editMode && childInvoices.length > 1 && (
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <button
                                            onClick={(e) => { e.stopPropagation(); pasteAllField('customer_name'); }}
                                            className="p-0.5 hover:bg-muted rounded"
                                            data-testid="paste-all-customer"
                                          >
                                            <Copy className="h-3 w-3 text-muted-foreground hover:text-primary" />
                                          </button>
                                        </TooltipTrigger>
                                        <TooltipContent><p className="text-xs">Paste to all rows</p></TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  )}
                                </div>
                              </th>
                              <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap" style={{ minWidth: '180px' }}>
                                <div className="flex items-center gap-1">
                                  Address
                                  {editMode && childInvoices.length > 1 && (
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <button
                                            onClick={(e) => { e.stopPropagation(); pasteAllField('unit_address'); }}
                                            className="p-0.5 hover:bg-muted rounded"
                                            data-testid="paste-all-address"
                                          >
                                            <Copy className="h-3 w-3 text-muted-foreground hover:text-primary" />
                                          </button>
                                        </TooltipTrigger>
                                        <TooltipContent><p className="text-xs">Paste to all rows</p></TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  )}
                                </div>
                              </th>
                              <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap" style={{ minWidth: '100px' }}>Account No</th>
                              <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap" style={{ minWidth: '85px' }}>Commodity</th>
                              <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap" style={{ minWidth: '75px' }}>Type</th>
                              <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap" style={{ minWidth: '110px' }}>Rate Schedule</th>
                              <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap" style={{ minWidth: '100px' }}>Invoice No</th>
                              <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap" style={{ minWidth: '95px' }}>
                                <div className="flex items-center gap-1">
                                  Due Date
                                  {editMode && childInvoices.length > 1 && (
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <button
                                            onClick={(e) => { e.stopPropagation(); pasteAllField('due_date'); }}
                                            className="p-0.5 hover:bg-muted rounded"
                                            data-testid="paste-all-due-date"
                                          >
                                            <Copy className="h-3 w-3 text-muted-foreground hover:text-primary" />
                                          </button>
                                        </TooltipTrigger>
                                        <TooltipContent><p className="text-xs">Paste to all rows</p></TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  )}
                                </div>
                              </th>
                              <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap" style={{ minWidth: '95px' }}>
                                <div className="flex items-center gap-1">
                                  Period From
                                  {editMode && childInvoices.length > 1 && (
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <button
                                            onClick={(e) => { e.stopPropagation(); pasteAllField('period_from'); }}
                                            className="p-0.5 hover:bg-muted rounded"
                                            data-testid="paste-all-period-from"
                                          >
                                            <Copy className="h-3 w-3 text-muted-foreground hover:text-primary" />
                                          </button>
                                        </TooltipTrigger>
                                        <TooltipContent><p className="text-xs">Paste to all rows</p></TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  )}
                                </div>
                              </th>
                              <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap" style={{ minWidth: '95px' }}>
                                <div className="flex items-center gap-1">
                                  Period To
                                  {editMode && childInvoices.length > 1 && (
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <button
                                            onClick={(e) => { e.stopPropagation(); pasteAllField('period_to'); }}
                                            className="p-0.5 hover:bg-muted rounded"
                                            data-testid="paste-all-period-to"
                                          >
                                            <Copy className="h-3 w-3 text-muted-foreground hover:text-primary" />
                                          </button>
                                        </TooltipTrigger>
                                        <TooltipContent><p className="text-xs">Paste to all rows</p></TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  )}
                                </div>
                              </th>
                              <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap" style={{ minWidth: '85px' }}>Bal B/F</th>
                              <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap" style={{ minWidth: '80px' }}>Sales Tax</th>
                              <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap" style={{ minWidth: '90px' }}>New Charges</th>
                              <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap" style={{ minWidth: '80px' }}>Credits</th>
                              <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap" style={{ minWidth: '80px' }}>Adjustments</th>
                              <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap" style={{ minWidth: '90px' }}>Total Due</th>
                              <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap" style={{ minWidth: '80px' }}>Status</th>
                              {editMode && <th className="p-2 w-8 border-b"></th>}
                            </tr>
                          </thead>
                          <tbody>
                            {childInvoices.map((invoice, index) => (
                              <tr 
                                key={index} 
                                className={`border-b border-border hover:bg-muted/25 ${invoice.page_number ? 'cursor-pointer' : ''} ${editingRowId === `invoices-${index}` ? 'bg-blue-50 ring-2 ring-blue-300 ring-inset' : ''}`}
                                onClick={() => !editMode && invoice.page_number && navigateToPage(invoice.page_number, invoice.child_account_no || invoice.unit_address)}
                                onFocus={() => {
                                  if (editMode) {
                                    setEditingRowId(`invoices-${index}`);
                                    if (invoice.page_number) navigateToPage(invoice.page_number, invoice.child_account_no || invoice.unit_address);
                                  }
                                }}
                                title={invoice.page_number ? `Click to go to page ${invoice.page_number}` : ''}
                              >
                                <td className="p-2 text-center">
                                  {invoice.page_number ? (
                                    <Badge variant="secondary" className="rounded-none text-[10px] px-1.5 py-0.5">
                                      p.{invoice.page_number}
                                    </Badge>
                                  ) : (
                                    <span className="text-xs text-muted-foreground">-</span>
                                  )}
                                </td>
                                <td className="p-2">
                                  {editMode ? (
                                    <Input
                                      value={invoice.customer_name || ''}
                                      onChange={(e) => updateChildInvoice(index, 'customer_name', e.target.value)}
                                      className="rounded-none h-8 text-xs min-w-[130px]"
                                    />
                                  ) : (
                                    <span className="whitespace-nowrap text-xs">{invoice.customer_name || '-'}</span>
                                  )}
                                </td>
                                <td className="p-2">
                                  {editMode ? (
                                    <Input
                                      value={invoice.unit_address || ''}
                                      onChange={(e) => updateChildInvoice(index, 'unit_address', e.target.value)}
                                      className="rounded-none h-8 text-xs min-w-[160px]"
                                    />
                                  ) : (
                                    <span className="whitespace-nowrap text-xs">{invoice.unit_address || '-'}</span>
                                  )}
                                </td>
                                <td className="p-2 font-mono">
                                  {editMode ? (
                                    <Input
                                      value={invoice.child_account_no || ''}
                                      onChange={(e) => updateChildInvoice(index, 'child_account_no', e.target.value)}
                                      className="rounded-none h-8 text-xs min-w-[90px]"
                                    />
                                  ) : (
                                    <span className="whitespace-nowrap text-xs">{invoice.child_account_no || '-'}</span>
                                  )}
                                </td>
                                <td className="p-2">
                                  {editMode ? (
                                    <Select
                                      value={invoice.commodity || 'Electricity'}
                                      onValueChange={(value) => updateChildInvoice(index, 'commodity', value)}
                                    >
                                      <SelectTrigger className="rounded-none h-8 w-24 text-xs">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="Electricity">Electricity</SelectItem>
                                        <SelectItem value="Gas">Gas</SelectItem>
                                        <SelectItem value="Water">Water</SelectItem>
                                        <SelectItem value="Sewer">Sewer</SelectItem>
                                        <SelectItem value="Waste">Waste</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  ) : (
                                    <Badge variant="outline" className="rounded-none text-xs">
                                      {invoice.commodity || '-'}
                                    </Badge>
                                  )}
                                </td>
                                <td className="p-2">
                                  {editMode ? (
                                    <Select
                                      value={invoice.service_type || 'Supply'}
                                      onValueChange={(value) => updateChildInvoice(index, 'service_type', value)}
                                    >
                                      <SelectTrigger className="rounded-none h-8 w-20 text-xs">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="Supply">Supply</SelectItem>
                                        <SelectItem value="Delivery">Delivery</SelectItem>
                                        <SelectItem value="Both">Both</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  ) : (
                                    <span className="text-xs">{invoice.service_type || '-'}</span>
                                  )}
                                </td>
                                <td className="p-2">
                                  {editMode ? (
                                    <Input
                                      value={invoice.rate_schedule || ''}
                                      onChange={(e) => updateChildInvoice(index, 'rate_schedule', e.target.value)}
                                      className="rounded-none h-8 text-xs min-w-[90px]"
                                    />
                                  ) : (
                                    <span className="text-xs">{invoice.rate_schedule || '-'}</span>
                                  )}
                                </td>
                                <td className="p-2 font-mono">
                                  {editMode ? (
                                    <Input
                                      value={invoice.child_invoice_no || ''}
                                      onChange={(e) => updateChildInvoice(index, 'child_invoice_no', e.target.value)}
                                      className="rounded-none h-8 text-xs min-w-[90px]"
                                    />
                                  ) : (
                                    <span className="whitespace-nowrap text-xs">{invoice.child_invoice_no || '-'}</span>
                                  )}
                                </td>
                                <td className="p-2">
                                  {editMode ? (
                                    <Input
                                      type="date"
                                      value={invoice.due_date || ''}
                                      onChange={(e) => updateChildInvoice(index, 'due_date', e.target.value)}
                                      className="rounded-none h-8 text-xs w-[110px]"
                                    />
                                  ) : (
                                    <div className="flex items-center gap-1">
                                      <span className="whitespace-nowrap text-xs">{invoice.due_date || '-'}</span>
                                      {invoiceDateValidations[index]?.due_date && (
                                        <TooltipProvider>
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <span>
                                                {invoiceDateValidations[index].due_date.isValid ? (
                                                  <CheckCircle className="h-3 w-3 text-green-500" />
                                                ) : (
                                                  <AlertTriangle className="h-3 w-3 text-amber-500" />
                                                )}
                                              </span>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                              <p className="text-xs">{invoiceDateValidations[index].due_date.isValid ? 'Valid' : invoiceDateValidations[index].due_date.message}</p>
                                            </TooltipContent>
                                          </Tooltip>
                                        </TooltipProvider>
                                      )}
                                    </div>
                                  )}
                                </td>
                                <td className="p-2">
                                  {editMode ? (
                                    <Input
                                      type="date"
                                      value={invoice.period_from || ''}
                                      onChange={(e) => updateChildInvoice(index, 'period_from', e.target.value)}
                                      className="rounded-none h-8 text-xs w-[110px]"
                                    />
                                  ) : (
                                    <div className="flex items-center gap-1">
                                      <span className="whitespace-nowrap text-xs">{invoice.period_from || '-'}</span>
                                      {invoiceDateValidations[index]?.period_from && (
                                        <TooltipProvider>
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <span>
                                                {invoiceDateValidations[index].period_from.isValid ? (
                                                  <CheckCircle className="h-3 w-3 text-green-500" />
                                                ) : (
                                                  <AlertTriangle className="h-3 w-3 text-amber-500" />
                                                )}
                                              </span>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                              <p className="text-xs">{invoiceDateValidations[index].period_from.isValid ? 'Valid' : invoiceDateValidations[index].period_from.message}</p>
                                            </TooltipContent>
                                          </Tooltip>
                                        </TooltipProvider>
                                      )}
                                    </div>
                                  )}
                                </td>
                                <td className="p-2">
                                  {editMode ? (
                                    <Input
                                      type="date"
                                      value={invoice.period_to || ''}
                                      onChange={(e) => updateChildInvoice(index, 'period_to', e.target.value)}
                                      className="rounded-none h-8 text-xs w-[110px]"
                                    />
                                  ) : (
                                    <div className="flex items-center gap-1">
                                      <span className="whitespace-nowrap text-xs">{invoice.period_to || '-'}</span>
                                      {invoiceDateValidations[index]?.period_to && invoiceDateValidations[index]?.period_range && (
                                        <TooltipProvider>
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <span>
                                                {invoiceDateValidations[index].period_to.isValid && invoiceDateValidations[index].period_range.isValid ? (
                                                  <CheckCircle className="h-3 w-3 text-green-500" />
                                                ) : (
                                                  <AlertTriangle className="h-3 w-3 text-amber-500" />
                                                )}
                                              </span>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                              <p className="text-xs">
                                                {invoiceDateValidations[index].period_to.isValid && invoiceDateValidations[index].period_range.isValid 
                                                  ? 'Valid' 
                                                  : invoiceDateValidations[index].period_to.message || invoiceDateValidations[index].period_range.message}
                                              </p>
                                            </TooltipContent>
                                          </Tooltip>
                                        </TooltipProvider>
                                      )}
                                    </div>
                                  )}
                                </td>
                                <td className="p-2">
                                  {editMode ? (
                                    <Input
                                      inputMode="decimal"
                                      value={invoice.balance_brought_forward ?? ''}
                                      onChange={(e) => {
                                        if (isValidAmountConsolidated(e.target.value)) {
                                          updateChildInvoice(index, 'balance_brought_forward', e.target.value);
                                        }
                                      }}
                                      className="rounded-none h-8 text-xs w-16"
                                      data-testid={`cons-child-bbf-${index}`}
                                    />
                                  ) : (
                                    <span className="text-xs">${invoice.balance_brought_forward || '0'}</span>
                                  )}
                                </td>
                                <td className="p-2">
                                  {editMode ? (
                                    <Input
                                      inputMode="decimal"
                                      value={invoice.sales_tax ?? ''}
                                      onChange={(e) => {
                                        if (isValidAmountConsolidated(e.target.value)) {
                                          updateChildInvoice(index, 'sales_tax', e.target.value);
                                        }
                                      }}
                                      className="rounded-none h-8 text-xs w-16"
                                      data-testid={`cons-child-salestax-${index}`}
                                    />
                                  ) : (
                                    <span className="text-xs">${invoice.sales_tax || '0'}</span>
                                  )}
                                </td>
                                <td className="p-2">
                                  {editMode ? (
                                    <Input
                                      inputMode="decimal"
                                      value={invoice.new_charges ?? ''}
                                      onChange={(e) => {
                                        if (isValidAmountConsolidated(e.target.value)) {
                                          updateChildInvoice(index, 'new_charges', e.target.value);
                                        }
                                      }}
                                      className="rounded-none h-8 text-xs w-16"
                                      data-testid={`cons-child-newcharges-${index}`}
                                    />
                                  ) : (
                                    <span className="text-xs">${invoice.new_charges || '0'}</span>
                                  )}
                                </td>
                                <td className="p-2">
                                  {editMode ? (
                                    <Input
                                      inputMode="decimal"
                                      value={invoice.credits ?? ''}
                                      onChange={(e) => {
                                        if (isValidAmountConsolidated(e.target.value)) {
                                          updateChildInvoice(index, 'credits', e.target.value);
                                        }
                                      }}
                                      className="rounded-none h-8 text-xs w-16"
                                      data-testid={`cons-child-credits-${index}`}
                                    />
                                  ) : (
                                    <span className="text-xs">${invoice.credits || '0'}</span>
                                  )}
                                </td>
                                <td className="p-2">
                                  {editMode ? (
                                    <Input
                                      inputMode="decimal"
                                      value={invoice.adjustments ?? ''}
                                      onChange={(e) => {
                                        if (isValidAmountConsolidated(e.target.value)) {
                                          updateChildInvoice(index, 'adjustments', e.target.value);
                                        }
                                      }}
                                      className="rounded-none h-8 text-xs w-16"
                                      data-testid={`cons-child-adjustments-${index}`}
                                    />
                                  ) : (
                                    <span className="text-xs">${invoice.adjustments || '0'}</span>
                                  )}
                                </td>
                                <td className="p-2 font-medium">
                                  {editMode ? (
                                    <Input
                                      inputMode="decimal"
                                      value={invoice.total_amount_due ?? ''}
                                      onChange={(e) => {
                                        if (isValidAmountConsolidated(e.target.value)) {
                                          updateChildInvoice(index, 'total_amount_due', e.target.value);
                                        }
                                      }}
                                      className="rounded-none h-8 text-xs w-16"
                                      data-testid={`cons-child-totaldue-${index}`}
                                    />
                                  ) : (
                                    <span className="text-xs">${invoice.total_amount_due || '0'}</span>
                                  )}
                                </td>
                                <td className="p-2">
                                  {editMode ? (
                                    <Select
                                      value={invoice.review_status || 'review'}
                                      onValueChange={(value) => updateChildInvoice(index, 'review_status', value)}
                                    >
                                      <SelectTrigger className="rounded-none h-8 w-20 text-xs">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="review">Review</SelectItem>
                                        <SelectItem value="approved">Approved</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  ) : (
                                    <Badge 
                                      variant={invoice.review_status === 'approved' ? 'default' : 'outline'}
                                      className={`rounded-none text-xs ${invoice.review_status === 'approved' ? 'bg-blue-600' : ''}`}
                                    >
                                      {invoice.review_status || 'Review'}
                                    </Badge>
                                  )}
                                </td>
                                {editMode && (
                                  <td className="p-2">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => removeChildInvoice(index)}
                                      className="h-8 w-8 p-0"
                                    >
                                      <Trash2 className="h-4 w-4 text-red-500" />
                                    </Button>
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </TabsContent>
                
                {/* Meter Tab */}
                <TabsContent value="meter" className="m-0 h-full overflow-hidden">
                  <div className="h-full flex flex-col p-4 overflow-auto">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <h3 className="text-sm font-bold uppercase tracking-wide text-muted-foreground">Meter Readings</h3>
                      </div>
                      {editMode && (
                        <Button size="sm" onClick={addMeterRead} className="rounded-none">
                          <Plus className="h-4 w-4 mr-1" /> Add Meter
                        </Button>
                      )}
                    </div>
                    
                    {meterReads.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        No meter readings found
                      </div>
                    ) : (
                      <>
                        <div className="flex-1 overflow-auto border border-border">
                          <table className="w-full text-sm min-w-[1500px]">
                            <thead className="bg-muted/50 sticky top-0">
                              <tr>
                                <th className="text-center p-2 text-xs font-medium border-b whitespace-nowrap" style={{ minWidth: '50px' }}>Page</th>
                                <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap">Account No</th>
                                <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap">Meter No</th>
                                <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap">Commodity</th>
                                <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap">Multiplier</th>
                                <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap">
                                  <div className="flex items-center gap-1">
                                    Prev Date
                                    {editMode && meterReads.length > 1 && (
                                      <TooltipProvider>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <button
                                              onClick={(e) => { e.stopPropagation(); pasteAllMeterField('previous_read_date'); }}
                                              className="p-0.5 hover:bg-muted rounded"
                                              data-testid="paste-all-prev-date"
                                            >
                                              <Copy className="h-3 w-3 text-muted-foreground hover:text-primary" />
                                            </button>
                                          </TooltipTrigger>
                                          <TooltipContent><p className="text-xs">Paste to all rows</p></TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                    )}
                                  </div>
                                </th>
                                <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap">
                                  <div className="flex items-center gap-1">
                                    Curr Date
                                    {editMode && meterReads.length > 1 && (
                                      <TooltipProvider>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <button
                                              onClick={(e) => { e.stopPropagation(); pasteAllMeterField('current_read_date'); }}
                                              className="p-0.5 hover:bg-muted rounded"
                                              data-testid="paste-all-curr-date"
                                            >
                                              <Copy className="h-3 w-3 text-muted-foreground hover:text-primary" />
                                            </button>
                                          </TooltipTrigger>
                                          <TooltipContent><p className="text-xs">Paste to all rows</p></TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                    )}
                                  </div>
                                </th>
                                <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap">Prev Read</th>
                                <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap">Curr Read</th>
                                <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap">Type</th>
                                {editMode && <th className="p-2 text-center text-xs font-medium border-b" style={{ minWidth: '70px' }}>Actions</th>}
                              </tr>
                            </thead>
                            <tbody>
                              {meterReads.map((meter, index) => {
                                const rowId = `meters-${index}`;
                                const isRowActive = editingRowId === rowId;
                                const isRowReviewed = reviewedRows.has(rowId);
                                const hasDateError = meterValidation.validations[index]?.isValid === false;
                                return (
                                  <Fragment key={index}>
                                  <tr 
                                    className={`border-b border-border transition-colors ${isRowActive ? 'bg-blue-50 ring-2 ring-blue-400 ring-inset' : ''} ${isRowReviewed ? 'bg-green-50' : ''} ${!editMode && meter.page_number ? 'cursor-pointer hover:bg-muted/25' : ''}`}
                                    onClick={() => !editMode && meter.page_number && navigateToPage(meter.page_number, meter.meter_number || meter.child_account_no)}
                                    title={meter.page_number ? `Click to go to page ${meter.page_number}` : ''}
                                  >
                                    <td className="p-2 text-center">
                                      {meter.page_number ? (
                                        <Badge 
                                          variant="secondary" 
                                          className={`rounded-none text-[10px] px-1.5 py-0.5 cursor-pointer hover:bg-muted ${isRowActive ? 'bg-blue-100' : ''}`}
                                          onClick={(e) => { e.stopPropagation(); navigateToPage(meter.page_number, meter.meter_number || meter.child_account_no); }}
                                        >
                                          p.{meter.page_number}
                                        </Badge>
                                      ) : (
                                        <span className="text-xs text-muted-foreground">-</span>
                                      )}
                                    </td>
                                    <td className="p-2 font-mono">
                                      {editMode ? (
                                        <Input
                                          value={meter.child_account_no || ''}
                                          onChange={(e) => updateMeterRead(index, 'child_account_no', e.target.value)}
                                          onFocus={() => handleRowFocus(rowId, meter.page_number)}
                                          className="rounded-none h-8 text-xs w-24"
                                        />
                                      ) : (
                                        <span className="text-xs">{meter.child_account_no || '-'}</span>
                                      )}
                                    </td>
                                    <td className="p-2 font-mono">
                                      {editMode ? (
                                        <Input
                                          inputMode="decimal"
                                          value={meter.meter_number || ''}
                                          onChange={(e) => {
                                            if (isNumericOnly(e.target.value)) {
                                              updateMeterRead(index, 'meter_number', e.target.value);
                                            }
                                          }}
                                          onFocus={() => handleRowFocus(rowId, meter.page_number)}
                                          className="rounded-none h-8 text-xs w-24"
                                          data-testid={`cons-meter-number-${index}`}
                                        />
                                      ) : (
                                        <span className="text-xs">{meter.meter_number || '-'}</span>
                                      )}
                                    </td>
                                    <td className="p-2">
                                      {editMode ? (
                                        <Select
                                          value={meter.commodity || 'Electricity'}
                                          onValueChange={(v) => updateMeterRead(index, 'commodity', v)}
                                          onOpenChange={(open) => open && handleRowFocus(rowId, meter.page_number)}
                                        >
                                          <SelectTrigger className="rounded-none h-8 text-xs w-24">
                                            <SelectValue />
                                          </SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="Electricity">Electricity</SelectItem>
                                            <SelectItem value="Gas">Gas</SelectItem>
                                            <SelectItem value="Water">Water</SelectItem>
                                            <SelectItem value="Sewer">Sewer</SelectItem>
                                          </SelectContent>
                                        </Select>
                                      ) : (
                                        <span className="text-xs">{meter.commodity || '-'}</span>
                                      )}
                                    </td>
                                    <td className="p-2">
                                      {editMode ? (
                                        <Input
                                          value={meter.multiplier || 'N/A'}
                                          onChange={(e) => updateMeterRead(index, 'multiplier', e.target.value)}
                                          className="rounded-none h-8 text-xs w-16"
                                          placeholder="N/A"
                                        />
                                      ) : (
                                        <span className="text-xs">{meter.multiplier || 'N/A'}</span>
                                      )}
                                    </td>
                                    <td className={`p-2 ${hasDateError ? 'bg-yellow-100' : ''}`}>
                                      {editMode ? (
                                        <Input
                                          type="date"
                                          value={meter.previous_read_date || ''}
                                          onChange={(e) => updateMeterRead(index, 'previous_read_date', e.target.value)}
                                          className="rounded-none h-8 text-xs w-28"
                                        />
                                      ) : (
                                        <span className="text-xs">{meter.previous_read_date || '-'}</span>
                                      )}
                                    </td>
                                    <td className={`p-2 ${hasDateError ? 'bg-yellow-100' : ''}`}>
                                      {editMode ? (
                                        <Input
                                          type="date"
                                          value={meter.current_read_date || ''}
                                          onChange={(e) => updateMeterRead(index, 'current_read_date', e.target.value)}
                                          className="rounded-none h-8 text-xs w-28"
                                        />
                                      ) : (
                                        <span className="text-xs">{meter.current_read_date || '-'}</span>
                                      )}
                                    </td>
                                    <td className="p-2">
                                      {editMode ? (
                                        <Input
                                          inputMode="decimal"
                                          value={meter.previous_read || ''}
                                          onChange={(e) => {
                                            if (isNumericOnly(e.target.value)) {
                                              updateMeterRead(index, 'previous_read', e.target.value);
                                            }
                                          }}
                                          className="rounded-none h-8 text-xs w-20"
                                          data-testid={`cons-meter-previous-read-${index}`}
                                        />
                                      ) : (
                                        <span className="text-xs">{meter.previous_read || '-'}</span>
                                      )}
                                    </td>
                                    <td className="p-2">
                                      {editMode ? (
                                        <Input
                                          inputMode="decimal"
                                          value={meter.current_read || ''}
                                          onChange={(e) => {
                                            if (isNumericOnly(e.target.value)) {
                                              updateMeterRead(index, 'current_read', e.target.value);
                                            }
                                          }}
                                          className="rounded-none h-8 text-xs w-20"
                                          data-testid={`cons-meter-current-read-${index}`}
                                        />
                                      ) : (
                                        <span className="text-xs">{meter.current_read || '-'}</span>
                                      )}
                                    </td>
                                    <td className="p-2">
                                      {editMode ? (
                                        <Select
                                          value={meter.read_type || 'Actual'}
                                          onValueChange={(value) => updateMeterRead(index, 'read_type', value)}
                                          onOpenChange={(open) => open && handleRowFocus(rowId, meter.page_number)}
                                        >
                                          <SelectTrigger className="rounded-none h-8 w-20 text-xs">
                                            <SelectValue />
                                          </SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="Actual">Actual</SelectItem>
                                            <SelectItem value="Estimated">Estimated</SelectItem>
                                          </SelectContent>
                                        </Select>
                                      ) : (
                                        <span className="text-xs">{meter.read_type || '-'}</span>
                                      )}
                                    </td>
                                    {editMode && (
                                      <td className="p-2 text-center">
                                        <div className="flex items-center justify-center gap-1">
                                          <Button
                                            size="sm"
                                            variant={isRowReviewed ? "default" : "outline"}
                                            className={`h-6 w-6 p-0 rounded-none ${isRowReviewed ? 'bg-green-600 hover:bg-green-700' : ''}`}
                                            onClick={() => toggleRowReviewed(rowId)}
                                            title={isRowReviewed ? 'Marked as reviewed' : 'Mark as reviewed'}
                                          >
                                            <Check className="h-3 w-3" />
                                          </Button>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => removeMeterRead(index)}
                                            className="h-6 w-6 p-0"
                                            title="Delete row"
                                          >
                                            <Trash2 className="h-3 w-3 text-red-500" />
                                          </Button>
                                        </div>
                                      </td>
                                    )}
                                  </tr>
                                  {Array.isArray(meter.reading_lines) && meter.reading_lines.length > 0 && (
                                    <tr data-testid={`cons-meter-reading-lines-${index}`}>
                                      <td className="p-0 bg-muted/20" colSpan={20}>
                                        <div className="px-3 py-2" data-testid={`cons-reading-lines-detail-${index}`}>
                                          <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                                            All meter readings ({meter.reading_lines.length})
                                          </div>
                                          <table className="w-full text-[11px] border-collapse">
                                            <thead>
                                              <tr className="bg-muted/60 text-muted-foreground">
                                                <th className="border border-border px-2 py-1 text-left">Read Dates</th>
                                                <th className="border border-border px-2 py-1 text-left">Load Type</th>
                                                <th className="border border-border px-2 py-1 text-left">Reading Type</th>
                                                <th className="border border-border px-2 py-1 text-right">Previous</th>
                                                <th className="border border-border px-2 py-1 text-right">Present</th>
                                                <th className="border border-border px-2 py-1 text-right">Difference</th>
                                                <th className="border border-border px-2 py-1 text-right">Mult</th>
                                                <th className="border border-border px-2 py-1 text-right">Total Usage</th>
                                                <th className="border border-border px-2 py-1 text-left">UOM</th>
                                                <th className="border border-border px-2 py-1 text-left">Type</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {meter.reading_lines.map((ln, li) => (
                                                <tr key={li} data-testid={`cons-reading-line-${index}-${li}`} className="font-mono">
                                                  <td className="border border-border px-2 py-1">{[ln.read_from, ln.read_to].filter(Boolean).join(' – ') || '—'}</td>
                                                  <td className="border border-border px-2 py-1 font-sans">{ln.load_type || '—'}</td>
                                                  <td className="border border-border px-2 py-1 font-sans font-medium">{ln.reading_type || '—'}</td>
                                                  <td className="border border-border px-2 py-1 text-right">{ln.previous_read ?? '—'}</td>
                                                  <td className="border border-border px-2 py-1 text-right">{ln.present_read ?? '—'}</td>
                                                  <td className="border border-border px-2 py-1 text-right">{ln.difference ?? '—'}</td>
                                                  <td className="border border-border px-2 py-1 text-right">{ln.multiplier ?? '—'}</td>
                                                  <td className="border border-border px-2 py-1 text-right">{ln.total_usage ?? '—'}</td>
                                                  <td className="border border-border px-2 py-1">{ln.uom || '—'}</td>
                                                  <td className="border border-border px-2 py-1 font-sans">{ln.actual_estimated || '—'}</td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                  </Fragment>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>

                        {/* Demand section — accordion so it doesn't shrink the meter table */}
                        <Accordion type="single" collapsible className="mt-3 border border-amber-200 bg-amber-50/30" data-testid="cons-demand-accordion">
                          <AccordionItem value="demand" className="border-0">
                            <AccordionTrigger className="px-3 py-2 text-xs font-bold uppercase tracking-wide text-amber-800 hover:no-underline" data-testid="cons-demand-accordion-trigger">
                              Demand Details ({meterReads.filter((m) => m.billed_demand !== null && m.billed_demand !== undefined && m.billed_demand !== '').length} of {meterReads.length} meters)
                            </AccordionTrigger>
                            <AccordionContent className="px-3 pb-3">
                              <DemandSummaryCard usageSummary={usageSummary} />
                              <div className="overflow-auto border border-border bg-white">
                                <table className="w-full text-sm">
                                  <thead className="bg-muted/50">
                                    <tr>
                                      <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap">Meter No</th>
                                      <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap">Account No</th>
                                      <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap">Commodity</th>
                                      <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap bg-amber-50">Billed Demand</th>
                                      <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap bg-amber-50">Dmd UOM</th>
                                      <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap bg-amber-50">Dmd Type</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {meterReads.map((meter, index) => {
                                      if (!editMode && (meter.billed_demand === null || meter.billed_demand === undefined || meter.billed_demand === '')) return null;
                                      return (
                                        <tr key={`dmd-${index}`} className="border-b hover:bg-muted/30">
                                          <td className="p-2 font-mono text-xs">{meter.meter_number || '-'}</td>
                                          <td className="p-2 font-mono text-xs">{meter.child_account_no || '-'}</td>
                                          <td className="p-2 text-xs">{meter.commodity || '-'}</td>
                                          <td className="p-2 bg-amber-50/40">
                                            {editMode ? (
                                              <Input
                                                className="rounded-none border-0 font-mono text-xs h-8 w-24"
                                                inputMode="decimal"
                                                value={meter.billed_demand ?? ''}
                                                onChange={(e) => {
                                                  if (isNumericOnly(e.target.value)) {
                                                    updateMeterRead(index, 'billed_demand', e.target.value);
                                                  }
                                                }}
                                                placeholder="—"
                                                data-testid={`cons-meter-billed-demand-${index}`}
                                              />
                                            ) : (
                                              <span className="text-xs font-mono">{meter.billed_demand ?? '-'}</span>
                                            )}
                                          </td>
                                          <td className="p-2 bg-amber-50/40">
                                            {editMode ? (
                                              <Select
                                                value={uomOptionsFor(meter.commodity || 'Electricity').includes(meter.demand_uom) ? meter.demand_uom : uomOptionsFor(meter.commodity || 'Electricity')[0]}
                                                onValueChange={(value) => updateMeterRead(index, 'demand_uom', value)}
                                              >
                                                <SelectTrigger className="rounded-none h-8 w-20 text-xs" data-testid={`cons-meter-demand-uom-trigger-${index}`}>
                                                  <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                  {uomOptionsFor(meter.commodity || 'Electricity').map((u) => (
                                                    <SelectItem key={u} value={u}>{u}</SelectItem>
                                                  ))}
                                                </SelectContent>
                                              </Select>
                                            ) : (
                                              <span className="text-xs">{meter.demand_uom ?? '-'}</span>
                                            )}
                                          </td>
                                          <td className="p-2 bg-amber-50/40">
                                            {editMode ? (
                                              <Input
                                                className="rounded-none border-0 font-mono text-xs h-8 w-20"
                                                value={meter.demand_read_type ?? ''}
                                                onChange={(e) => updateMeterRead(index, 'demand_read_type', e.target.value)}
                                                placeholder="—"
                                              />
                                            ) : (
                                              <span className="text-xs">{meter.demand_read_type ?? '-'}</span>
                                            )}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        </Accordion>
                      </>
                    )}
                  </div>
                </TabsContent>
                
                {/* Usage Tab */}
                <TabsContent value="usage" className="m-0 h-full overflow-hidden">
                  <div className="h-full flex flex-col p-4">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="text-sm font-bold uppercase tracking-wide text-muted-foreground">Usage Details</h3>
                        <p className="text-xs text-muted-foreground mt-1">
                          Total usage with original UOM, rate and charge • {filteredUsageDetails.length} items
                        </p>
                      </div>
                      {editMode && (
                        <Button size="sm" onClick={addUsageDetail} className="rounded-none">
                          <Plus className="h-4 w-4 mr-1" /> Add Usage
                        </Button>
                      )}
                    </div>
                    
                    {filteredUsageDetails.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        No usage details found
                      </div>
                    ) : (
                      <>
                        <div className="flex-1 overflow-auto border border-border">
                          <table className="w-full text-sm min-w-[1250px]">
                            <thead className="bg-muted/50 sticky top-0">
                              <tr>
                                <th className="text-center p-2 text-xs font-medium border-b whitespace-nowrap" style={{ minWidth: '50px' }}>Page</th>
                                <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap">Account No</th>
                                <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap">Meter No</th>
                                <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap">Commodity</th>
                                <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap">Type</th>
                                <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap" style={{ minWidth: '160px' }}>Description</th>
                                <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap">Usage</th>
                                <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap">UOM</th>
                                <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap">Rate</th>
                                <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap">Charge</th>
                                {editMode && <th className="p-2 w-8 border-b"></th>}
                              </tr>
                            </thead>
                            <tbody>
                              {filteredUsageDetails.map((usage, index) => {
                                return (
                                  <tr 
                                    key={index} 
                                    className={`border-b border-border hover:bg-muted/25 ${usage.page_number ? 'cursor-pointer' : ''} ${editingRowId === `usage-${index}` ? 'bg-blue-50 ring-2 ring-blue-300 ring-inset' : ''}`}
                                    onClick={() => !editMode && usage.page_number && navigateToPage(usage.page_number, usage.line_item_description || usage.meter_number)}
                                    onFocus={() => {
                                      if (editMode) {
                                        setEditingRowId(`usage-${index}`);
                                        if (usage.page_number) navigateToPage(usage.page_number, usage.line_item_description || usage.meter_number);
                                      }
                                    }}
                                    title={usage.page_number ? `Click to go to page ${usage.page_number}` : ''}
                                  >
                                    <td className="p-2 text-center">
                                      {usage.page_number ? (
                                        <Badge variant="secondary" className="rounded-none text-[10px] px-1.5 py-0.5">
                                          p.{usage.page_number}
                                        </Badge>
                                      ) : (
                                        <span className="text-xs text-muted-foreground">-</span>
                                      )}
                                </td>
                                <td className="p-2 font-mono">
                                  {editMode ? (
                                    <Input
                                      value={usage.child_account_no || ''}
                                      onChange={(e) => updateUsageDetail(index, 'child_account_no', e.target.value)}
                                      className="rounded-none h-8 text-xs w-24"
                                    />
                                  ) : (
                                    <span className="whitespace-nowrap text-xs">{usage.child_account_no || '-'}</span>
                                  )}
                                </td>
                                <td className="p-2 font-mono">
                                  {editMode ? (
                                    <Input
                                      inputMode="decimal"
                                      value={usage.meter_number || ''}
                                      onChange={(e) => {
                                        if (isNumericOnly(e.target.value)) {
                                          updateUsageDetail(index, 'meter_number', e.target.value);
                                        }
                                      }}
                                      className="rounded-none h-8 text-xs w-24"
                                      data-testid={`cons-usage-meter-number-${index}`}
                                    />
                                  ) : (
                                    <span className="whitespace-nowrap text-xs">{usage.meter_number || '-'}</span>
                                  )}
                                </td>
                                <td className="p-2">
                                  {editMode ? (
                                    <Select
                                      value={usage.commodity || 'Electricity'}
                                      onValueChange={(value) => updateUsageDetail(index, 'commodity', value)}
                                    >
                                      <SelectTrigger className="rounded-none h-8 w-24 text-xs">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="Electricity">Electricity</SelectItem>
                                        <SelectItem value="Gas">Gas</SelectItem>
                                        <SelectItem value="Water">Water</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  ) : (
                                    <Badge variant="outline" className="rounded-none text-xs">
                                      {usage.commodity || '-'}
                                    </Badge>
                                  )}
                                </td>
                                <td className="p-2">
                                  {editMode ? (
                                    <Select
                                      value={usage.usage_type || 'Supply'}
                                      onValueChange={(value) => updateUsageDetail(index, 'usage_type', value)}
                                    >
                                      <SelectTrigger className="rounded-none h-8 w-20 text-xs">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="Supply">Supply</SelectItem>
                                        <SelectItem value="Delivery">Delivery</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  ) : (
                                    <span className="text-xs">{usage.usage_type || '-'}</span>
                                  )}
                                </td>
                                <td className="p-2">
                                  {editMode ? (
                                    <Input
                                      value={usage.line_item_description || ''}
                                      onChange={(e) => updateUsageDetail(index, 'line_item_description', e.target.value)}
                                      className="rounded-none h-8 text-xs"
                                      style={{ minWidth: '140px' }}
                                    />
                                  ) : (
                                    <span className="text-xs inline-flex items-center gap-1">
                                      {usage.line_item_description || '-'}
                                      <BillingBasisBadge basis={usage.billing_basis} />
                                    </span>
                                  )}
                                </td>
                                <td className="p-2">
                                  {editMode ? (
                                    <Input
                                      inputMode="decimal"
                                      value={usage.usage_quantity || '0'}
                                      onChange={(e) => {
                                        if (isNumericOnly(e.target.value)) {
                                          updateUsageDetail(index, 'usage_quantity', e.target.value);
                                        }
                                      }}
                                      className="rounded-none h-8 text-xs w-20"
                                      data-testid={`cons-usage-quantity-${index}`}
                                    />
                                  ) : (
                                    <span className="text-xs font-semibold">{usage.usage_quantity || '0'}</span>
                                  )}
                                </td>
                                <td className="p-2">
                                  {editMode ? (
                                    <Select
                                      value={uomOptionsFor(usage.commodity || 'Electricity').includes(usage.uom) ? usage.uom : uomOptionsFor(usage.commodity || 'Electricity')[0]}
                                      onValueChange={(value) => updateUsageDetail(index, 'uom', value)}
                                    >
                                      <SelectTrigger className="rounded-none h-8 text-xs w-20" data-testid={`cons-usage-uom-trigger-${index}`}>
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {uomOptionsFor(usage.commodity || 'Electricity').map((u) => (
                                          <SelectItem key={u} value={u}>{u}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  ) : (
                                    <span className="text-xs">{usage.uom || '-'}</span>
                                  )}
                                </td>
                                <td className="p-2">
                                  {editMode ? (
                                    <Input
                                      inputMode="decimal"
                                      value={usage.rate || '0'}
                                      onChange={(e) => {
                                        if (isNumericOnly(e.target.value)) {
                                          updateUsageDetail(index, 'rate', e.target.value);
                                        }
                                      }}
                                      className="rounded-none h-8 text-xs w-16"
                                      data-testid={`cons-usage-rate-${index}`}
                                    />
                                  ) : (
                                    <span className="text-xs">{usage.rate || '0'}</span>
                                  )}
                                </td>
                                <td className="p-2 font-medium">
                                  {editMode ? (
                                    <Input
                                      inputMode="decimal"
                                      value={usage.line_item_charge || '0'}
                                      onChange={(e) => {
                                        if (isNumericOnly(e.target.value)) {
                                          updateUsageDetail(index, 'line_item_charge', e.target.value);
                                        }
                                      }}
                                      className="rounded-none h-8 text-xs w-20"
                                      data-testid={`cons-usage-charge-${index}`}
                                    />
                                  ) : (
                                    <span className="text-xs">${usage.line_item_charge || '0'}</span>
                                  )}
                                </td>
                                {editMode && (
                                  <td className="p-2">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => removeUsageDetail(index)}
                                      className="h-8 w-8 p-0"
                                    >
                                      <Trash2 className="h-4 w-4 text-red-500" />
                                    </Button>
                                  </td>
                                )}
                              </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}
                  </div>
                </TabsContent>

                {/* Other Charges Tab */}
                <TabsContent value="other" className="m-0 p-6 h-full overflow-auto" data-testid="cons-other-tab-content">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold">Other Charges</h3>
                    {editMode && (
                      <Button size="sm" variant="outline" className="rounded-none" onClick={addOtherCharge} data-testid="cons-add-other-charge">
                        <Plus className="h-4 w-4 mr-1" /> Add Row
                      </Button>
                    )}
                  </div>

                  {otherCharges.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-12" data-testid="cons-other-empty">No other charges</p>
                  ) : (
                    <div className="overflow-x-auto border border-border">
                      <table className="w-full border-collapse">
                        <thead className="bg-muted">
                          <tr>
                            <th className="border border-border p-2 text-left text-xs font-medium">Page</th>
                            <th className="border border-border p-2 text-left text-xs font-medium">Account No</th>
                            <th className="border border-border p-2 text-left text-xs font-medium">Line Item Description</th>
                            <th className="border border-border p-2 text-left text-xs font-medium">Usage</th>
                            <th className="border border-border p-2 text-left text-xs font-medium">Rate</th>
                            <th className="border border-border p-2 text-left text-xs font-medium">Charge</th>
                            {editMode && <th className="border border-border p-2 w-20 text-center text-xs font-medium">Actions</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {otherCharges.map((item, index) => (
                            <tr key={index} data-testid={`cons-other-row-${index}`}>
                              <td className="border border-border p-1 text-center">
                                {item.page_number && (
                                  <Badge variant="outline" className="rounded-none text-xs font-mono">
                                    p.{item.page_number}
                                  </Badge>
                                )}
                              </td>
                              <td className="border border-border p-1">
                                <Input
                                  className="rounded-none border-0 font-mono text-xs"
                                  value={item.child_account_no || ''}
                                  onChange={(e) => updateOtherCharge(index, 'child_account_no', e.target.value)}
                                  disabled={!editMode}
                                  placeholder="—"
                                  data-testid={`cons-other-acct-${index}`}
                                />
                              </td>
                              <td className="border border-border p-1">
                                <Input
                                  className="rounded-none border-0 font-mono text-xs"
                                  value={item.line_item_description || ''}
                                  onChange={(e) => updateOtherCharge(index, 'line_item_description', e.target.value)}
                                  disabled={!editMode}
                                  data-testid={`cons-other-desc-${index}`}
                                />
                              </td>
                              <td className="border border-border p-1">
                                <Input
                                  className="rounded-none border-0 font-mono text-xs"
                                  inputMode="decimal"
                                  value={item.usage || ''}
                                  onChange={(e) => {
                                    if (isNumericOnly(e.target.value)) updateOtherCharge(index, 'usage', e.target.value);
                                  }}
                                  disabled={!editMode}
                                  data-testid={`cons-other-usage-${index}`}
                                />
                              </td>
                              <td className="border border-border p-1">
                                <Input
                                  className="rounded-none border-0 font-mono text-xs"
                                  inputMode="decimal"
                                  value={item.rate || ''}
                                  onChange={(e) => {
                                    if (isNumericOnly(e.target.value)) updateOtherCharge(index, 'rate', e.target.value);
                                  }}
                                  disabled={!editMode}
                                  data-testid={`cons-other-rate-${index}`}
                                />
                              </td>
                              <td className="border border-border p-1">
                                <Input
                                  className="rounded-none border-0 font-mono text-xs"
                                  inputMode="decimal"
                                  value={item.charge || ''}
                                  onChange={(e) => {
                                    if (isNumericOnly(e.target.value)) updateOtherCharge(index, 'charge', e.target.value);
                                  }}
                                  disabled={!editMode}
                                  data-testid={`cons-other-charge-${index}`}
                                />
                              </td>
                              {editMode && (
                                <td className="border border-border p-1 text-center">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-6 w-6 p-0"
                                    onClick={() => removeOtherCharge(index)}
                                    data-testid={`cons-other-remove-${index}`}
                                    title="Delete row"
                                  >
                                    <X className="h-4 w-4 text-red-500" />
                                  </Button>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <div className="border border-border border-t-0 bg-muted/50 p-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">Total Other Charges:</span>
                          <span className="text-lg font-bold font-mono" data-testid="cons-other-total">
                            ${consValidation.otherSum.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </TabsContent>

                {/* Validation Tab */}
                <TabsContent value="validation" className="m-0 p-6 h-full overflow-auto" data-testid="cons-validation-tab-content">
                  <ValidationTab
                    mandatoryFieldsValidation={mandatoryFieldsValidation}
                    consolidatedValidation={consolidatedValidation}
                    consValidation={consValidation}
                    childReconciliation={childReconciliation}
                    summaryMatrix={summaryMatrix}
                    meterCompleteness={meterCompleteness}
                    accordionValue={validationAccordionValue}
                    onAccordionChange={setValidationAccordion}
                    onRevalidate={handleRevalidate}
                    revalidating={revalidating}
                  />
                </TabsContent>
              </div>
            </Tabs>
          </div>
        </ResizablePanel>
      </ResizablePanelGroup>
      
      {/* Audit Log Modal */}
      <Dialog open={showAuditModal} onOpenChange={setShowAuditModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Document History
            </DialogTitle>
            <DialogDescription>
              Complete audit trail for this document
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-3">
            {auditLogs.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No history available</p>
            ) : (
              auditLogs.map((log, index) => {
                const logDate = log.created_at || log.timestamp;
                const formattedDate = logDate ? format(new Date(logDate), 'MMM d, yyyy h:mm a') : '';
                return (
                  <div key={log.id || index} className="border border-border rounded-none p-3 bg-card">
                    <div className="flex items-center justify-between mb-2">
                      <Badge variant="outline" className="rounded-none text-xs font-mono">
                        {(log.action || '').replace(/_/g, ' ')}
                      </Badge>
                      <span className="text-xs text-muted-foreground">{formattedDate}</span>
                    </div>
                    <p className="text-sm">
                      <span className="font-medium">{log.user_name}</span>
                      {log.action === 'status_changed' && log.changes && (
                        <span> changed status from <Badge variant="secondary" className="rounded-none text-xs mx-1">{log.changes.from}</Badge> to <Badge variant="secondary" className="rounded-none text-xs mx-1">{log.changes.to}</Badge></span>
                      )}
                      {log.action === 'assignment_changed' && log.changes && (
                        <span> {log.changes.assigned_to ? `assigned document to ${log.changes.assigned_to_name || 'a user'}` : 'removed assignment'}</span>
                      )}
                      {log.action === 'edited' && <span> updated extracted data</span>}
                      {log.action === 'reprocessed' && <span> triggered document re-parsing</span>}
                      {log.action === 'uploaded' && <span> uploaded this document</span>}
                    </p>
                    {log.changes && typeof log.changes === 'object' && !['from', 'to', 'assigned_to'].some(k => k in log.changes) && (
                      <details className="mt-2">
                        <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground" data-testid={`cons-audit-view-changes-${index}`}>
                          View changes ({Object.keys(log.changes).length} field{Object.keys(log.changes).length === 1 ? '' : 's'})
                        </summary>
                        <div className="mt-2 text-xs bg-muted p-2 rounded-none max-h-48 overflow-auto">
                          {Object.entries(log.changes).map(([field, values]) => {
                            const label = (values && typeof values === 'object' && values.label) || field;
                            const isDiff = values && typeof values === 'object' && ('old' in values || 'new' in values);
                            const oldV = isDiff ? String(values.old ?? '') : '';
                            const newV = isDiff ? String(values.new ?? '') : '';
                            return (
                              <div key={field} className="mb-1.5 flex flex-wrap items-baseline gap-1" data-testid={`cons-audit-change-${field}`}>
                                <span className="font-medium text-foreground">{label}:</span>
                                {isDiff ? (
                                  <>
                                    <span className="line-through text-red-600 font-mono break-all">{oldV === '' ? '(empty)' : oldV}</span>
                                    <span className="text-muted-foreground">→</span>
                                    <span className="text-green-700 font-mono break-all">{newV === '' ? '(empty)' : newV}</span>
                                  </>
                                ) : (
                                  <span className="text-foreground font-mono break-all">{Array.isArray(values) ? values.join(', ') : String(values)}</span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </details>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ConsolidatedViewer;
